<template>
  <v-dialog v-model="dialogOpen" :max-width="activeTab === 'excel' ? '900' : '520'" content-class="cashbook-upload-dialog" persistent>
    <div class="cashbook-upload-modal" data-tour="bb-test-upload-dialog" :class="{ 'excel-mode': activeTab === 'excel' }">
      <!-- 모달 헤더 -->
      <div class="modal-header">
        <div class="header-icon">
          <v-icon size="24" color="white">mdi-upload</v-icon>
        </div>
        <div class="header-text">
          <h3>{{ getHeaderTitle }}</h3>
          <p>{{ getHeaderSubtitle }}</p>
        </div>
        <!-- 도움말 버튼 -->
        <button class="help-btn" @click="showHelpDialog = true" title="사용 설명서">
          <v-icon size="18">mdi-help-circle-outline</v-icon>
        </button>
        <button class="close-btn" @click="closeDialog" :disabled="isProcessing && !processComplete">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>

      <!-- 탭 네비게이션 -->
      <div class="modal-tabs">
        <button 
          type="button" 
          class="modal-tab excel" 
          :class="{ active: activeTab === 'excel' }"
          :disabled="isProcessing && !processComplete"
          @click="activeTab = 'excel'"
        >
          <v-icon size="16">mdi-file-excel</v-icon>
          엑셀 업로드
        </button>
        <button 
          type="button" 
          class="modal-tab" 
          :class="{ active: activeTab === 'input' }"
          :disabled="isProcessing && !processComplete"
          @click="activeTab = 'input'"
        >
          <v-icon size="16">mdi-plus-circle</v-icon>
          직접 입력
        </button>
      </div>

      <!-- 직접 입력 탭 -->
      <div v-show="activeTab === 'input'" class="modal-body">
        <div class="auto-notice">
          <v-icon size="16" color="#64748b">mdi-information-outline</v-icon>
          <span>통장·일자·금액·적요를 입력해 저장합니다. 계정은 통장내역 화면에서 지정할 수 있습니다.</span>
        </div>

        <v-form ref="formRef">
          <!-- 구분 선택 -->
          <div class="form-section">
            <div class="gubun-tabs">
              <button 
                type="button"
                class="gubun-tab income" 
                :class="{ active: formData.gubun === 'IN' }"
                @click="formData.gubun = 'IN'"
              >
                <v-icon size="16">mdi-plus-circle</v-icon>
                수입
              </button>
              <button 
                type="button"
                class="gubun-tab expense" 
                :class="{ active: formData.gubun === 'OUT' }"
                @click="formData.gubun = 'OUT'"
              >
                <v-icon size="16">mdi-minus-circle</v-icon>
                지출
              </button>
            </div>
          </div>

          <!-- 통장 선택 -->
          <div v-if="activeBankList.length > 0" class="form-group">
            <label class="form-label required">통장</label>
            <select 
              v-model="formData.bankCode" 
              class="form-input bank-select"
            >
              <option value="">통장을 선택하세요</option>
              <option 
                v-for="bank in activeBankList" 
                :key="bank.mb_code" 
                :value="bank.mb_code"
              >
                {{ bank.mb_alias }} ({{ bank.mb_bank_name }})
              </option>
            </select>
          </div>

          <!-- 날짜 / 금액 -->
          <div class="form-row two-cols compact">
            <div class="form-group">
              <label class="form-label required">출납일자</label>
              <YmdDateInput v-model="formData.inout_date" class="form-input" />
            </div>
            <div class="form-group">
              <label class="form-label required">금액</label>
              <div class="price-input-box" :class="{ focus: priceFocused }">
                <input
                  type="text"
                  class="price-field"
                  :value="formattedPrice"
                  @input="handlePriceInput"
                  @keydown="handlePriceKeydown"
                  @focus="priceFocused = true"
                  @blur="priceFocused = false"
                  placeholder="0"
                />
                <span class="price-unit">원</span>
              </div>
            </div>
          </div>

          <!-- 삽입 위치 선택 (해당 날짜에 기존 내역이 있는 경우) -->
          <div v-if="formData.inout_date && existingItemsForDate.length > 0" class="form-group insert-position-group">
            <label class="form-label">
              삽입 위치
              <span class="existing-count">(기존 {{ existingItemsForDate.length }}건)</span>
            </label>
            <div class="insert-position-options">
              <label class="position-option" :class="{ active: insertPosition === 'first' }">
                <input type="radio" v-model="insertPosition" value="first" />
                <span class="option-label">맨 앞</span>
              </label>
              <template v-for="(item, idx) in existingItemsForDate" :key="item.cb_idx">
                <label class="position-option existing-item" :class="{ active: insertPosition === item.cb_idx }">
                  <input type="radio" v-model="insertPosition" :value="item.cb_idx" />
                  <span class="option-order">{{ idx + 1 }}</span>
                  <span class="option-gubun" :class="item.cb_gubun === 'IN' ? 'income' : 'expense'">
                    {{ item.cb_gubun === 'IN' ? '입' : '출' }}
                  </span>
                  <span class="option-memo">{{ item.cb_memo?.substring(0, 15) || '(적요없음)' }}{{ item.cb_memo?.length > 15 ? '...' : '' }}</span>
                  <span class="option-price">{{ Number(item.cb_price || 0).toLocaleString() }}</span>
                  <span class="option-suffix">뒤에</span>
                </label>
              </template>
              <label class="position-option" :class="{ active: insertPosition === 'last' }">
                <input type="radio" v-model="insertPosition" value="last" />
                <span class="option-label">맨 뒤</span>
                <span class="option-default">(기본)</span>
              </label>
            </div>
          </div>
          <div v-else-if="isLoadingExistingItems" class="form-group">
            <div class="loading-existing">
              <v-progress-circular indeterminate size="16" width="2" />
              <span>기존 내역 확인 중...</span>
            </div>
          </div>

          <!-- 적요 -->
          <div class="form-group">
            <label class="form-label required">적요</label>
            <input
              type="text"
              class="form-input"
              v-model="formData.memo"
              placeholder="적요를 입력하세요 (예: 직원음료, 본인부담금 등)"
            />
          </div>

          <!-- 상대계정 (지출만) -->
          <div v-if="formData.gubun === 'OUT'" class="form-group">
            <label class="form-label">상대계정</label>
            <v-autocomplete
              v-model="formData.relAcc"
              :items="props.incomeCodeList || []"
              item-title="sname"
              item-value="scode"
              variant="outlined"
              density="compact"
              hide-details
              placeholder="어떤 수입에서 지출하는지 선택"
              clearable
            />
          </div>
        </v-form>
      </div>

      <!-- 엑셀 업로드 탭 -->
      <div v-show="activeTab === 'excel'" class="modal-body excel-body">
        <div class="excel-upload-section">
          <!-- 통장 없음 경고 -->
          <div v-if="!props.bankList || props.bankList.length === 0" class="no-bank-warning">
            <v-icon size="24" color="#f59e0b">mdi-alert-circle</v-icon>
            <div class="warning-text">
              <p>등록된 통장이 없습니다.</p>
              <span>먼저 "통장 관리"에서 통장을 등록해주세요.</span>
            </div>
          </div>
          
          <!-- 엑셀 업로드 영역 -->
          <template v-else>
              <div class="quick-upload-mode">
                
                <!-- STEP 1: 파일 선택 -->
                <template v-if="quickUploadStep === 'select'">
                  <div class="quick-upload-guide">
                    <v-icon size="16" color="#1565c0">mdi-information</v-icon>
                    <span>통장별로 엑셀(또는 CSV) 파일을 선택한 뒤 업로드하세요. 계정은 통장내역 화면에서 지정할 수 있습니다.</span>
                  </div>
                  
                  <!-- 통장별 파일 선택 목록 -->
                  <div class="bank-file-list">
                    <div 
                      v-for="bank in activeBankList" 
                      :key="bank.mb_code"
                      class="bank-file-item"
                      :class="{ 'has-file': quickUploadFiles[bank.mb_code] }"
                    >
                      <div class="bank-info-col">
                        <v-icon size="18" color="#1565c0">mdi-bank</v-icon>
                        <div class="bank-details">
                          <span class="bank-alias">{{ bank.mb_alias }}</span>
                          <span class="bank-name">{{ bank.mb_bank_name }}</span>
                        </div>
                      </div>
                      <div class="file-col">
                        <div 
                          class="file-dropzone-mini"
                          :class="{ 'has-file': quickUploadFiles[bank.mb_code] }"
                          @click="triggerQuickFileInput(bank.mb_code)"
                        >
                          <input 
                            :ref="el => setQuickFileInputRef(el, bank.mb_code)"
                            type="file" 
                            accept=".xlsx,.xls,.csv"
                            @change="e => handleQuickFileSelect(e, bank.mb_code)"
                            hidden
                          />
                          <template v-if="!quickUploadFiles[bank.mb_code]">
                            <v-icon size="16" color="#94a3b8">mdi-file-plus</v-icon>
                            <span>파일 선택</span>
                          </template>
                          <template v-else>
                            <v-icon size="16" color="#10b981">mdi-file-excel</v-icon>
                            <span class="file-name">{{ quickUploadFiles[bank.mb_code].name }}</span>
                            <button type="button" class="btn-remove-mini" @click.stop="removeQuickFile(bank.mb_code)">
                              <v-icon size="12">mdi-close</v-icon>
                            </button>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- 다음 단계 버튼 -->
                  <div class="quick-upload-actions">
                    <div class="upload-summary">
                      <span v-if="quickUploadFileCount === 0" class="no-files">파일을 선택해주세요</span>
                      <span v-else class="has-files">
                        <v-icon size="14" color="#10b981">mdi-check-circle</v-icon>
                        {{ quickUploadFileCount }}개 파일 선택됨
                      </span>
                    </div>
                    <button 
                      type="button" 
                      class="btn-quick-upload"
                      :disabled="quickUploadFileCount === 0 || isAnalyzingFiles"
                      @click="analyzeQuickUploadFiles"
                    >
                      <v-progress-circular v-if="isAnalyzingFiles" indeterminate size="18" width="2" color="white" />
                      <template v-else>
                        <v-icon size="18">mdi-table-search</v-icon>
                        헤더 확인
                      </template>
                    </button>
                  </div>
                </template>
                
                <!-- STEP 2: 헤더 확인 및 컬럼 매핑 -->
                <template v-else-if="quickUploadStep === 'confirm'">
                  <div class="quick-confirm-header">
                    <button type="button" class="btn-back" @click="quickUploadStep = 'select'">
                      <v-icon size="16">mdi-arrow-left</v-icon>
                      파일 선택으로
                    </button>
                    <div class="confirm-title">
                      <v-icon size="18" color="#1565c0">mdi-table-check</v-icon>
                      <span>헤더 확인 ({{ quickFileAnalyses.length }}개 파일)</span>
                    </div>
                  </div>
                  
                  <div class="quick-confirm-guide">
                    <v-icon size="14" color="#f59e0b">mdi-alert</v-icon>
                    <span>각 파일의 컬럼 매핑을 확인하세요. 빨간색 표시는 필수 컬럼이 감지되지 않은 파일입니다.</span>
                  </div>
                  
                  <!-- 파일별 헤더 확인 목록 -->
                  <div class="quick-file-analyses">
                    <div 
                      v-for="(analysis, idx) in quickFileAnalyses" 
                      :key="analysis.bankCode"
                      class="file-analysis-card"
                      :class="{ 'is-invalid': !analysis.isValid, 'is-expanded': currentAnalysisIndex === idx }"
                    >
                      <!-- 카드 헤더 (클릭으로 펼치기/접기) -->
                      <div class="analysis-card-header" @click="toggleAnalysisCard(idx)">
                        <div class="card-left">
                          <v-icon 
                            size="18" 
                            :color="analysis.isValid ? '#10b981' : '#ef4444'"
                          >
                            {{ analysis.isValid ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                          </v-icon>
                          <div class="card-info">
                            <span class="card-bank">{{ analysis.bankAlias }}</span>
                            <span class="card-file">{{ analysis.file.name }}</span>
                          </div>
                        </div>
                        <div class="card-right">
                          <span v-if="analysis.isValid" class="mapping-summary">
                            날짜: {{ analysis.columnMapping.date !== undefined ? '✓' : '✗' }}
                            | 금액: {{ (analysis.columnMapping.income !== undefined || analysis.columnMapping.amount !== undefined) ? '✓' : '✗' }}
                            | 적요: {{ analysis.selectedMemoColumns.length > 0 ? '✓' : '✗' }}
                          </span>
                          <span v-else class="error-badge">{{ analysis.errorMessage }}</span>
                          <v-icon size="18" color="#64748b">
                            {{ currentAnalysisIndex === idx ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                          </v-icon>
                        </div>
                      </div>
                      
                      <!-- 카드 본문 (펼쳐졌을 때) -->
                      <div v-if="currentAnalysisIndex === idx" class="analysis-card-body">
                        
                        <!-- ===== STEP 1: 헤더 행 선택 ===== -->
                        <div v-if="analysis.step === 'header'" class="header-selection-step">
                          <div class="step-title">
                            <span class="step-badge">1</span>
                            <span>헤더 행 선택</span>
                            <span class="step-hint">날짜, 금액 등 컬럼명이 있는 행을 클릭하세요</span>
                          </div>
                          
                          <!-- 엑셀 스타일 테이블 -->
                          <div class="excel-preview-wrapper">
                            <table class="excel-preview-table">
                              <thead>
                                <tr>
                                  <th class="col-select"></th>
                                  <th class="col-rownum"></th>
                                  <th 
                                    v-for="colIdx in getMaxColumns(analysis.previewRows)" 
                                    :key="colIdx" 
                                    class="col-header"
                                  >
                                    {{ getExcelColumnName(colIdx - 1) }}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr 
                                  v-for="(row, rowIdx) in analysis.previewRows" 
                                  :key="rowIdx"
                                  :class="{ 
                                    selected: analysis.selectedHeaderRowIndex === rowIdx,
                                    recommended: analysis.suggestedHeaderRowIndex === rowIdx
                                  }"
                                  @click="selectHeaderRow(idx, rowIdx)"
                                >
                                  <td class="col-select">
                                    <div class="select-indicator">
                                      <v-icon v-if="analysis.selectedHeaderRowIndex === rowIdx" size="16" color="#3b82f6">mdi-check-circle</v-icon>
                                      <span 
                                        v-else-if="analysis.suggestedHeaderRowIndex === rowIdx" 
                                        class="recommend-badge"
                                        :class="analysis.detectionConfidence"
                                      >
                                        {{ analysis.detectionConfidence === 'high' ? '추천' : analysis.detectionConfidence === 'medium' ? '추정' : '?' }}
                                      </span>
                                      <v-icon v-else size="16" color="#cbd5e1">mdi-circle-outline</v-icon>
                                    </div>
                                  </td>
                                  <td class="col-rownum">{{ rowIdx + 1 }}</td>
                                  <td 
                                    v-for="colIdx in getMaxColumns(analysis.previewRows)" 
                                    :key="colIdx"
                                    class="col-data"
                                  >
                                    {{ row[colIdx - 1] !== undefined && row[colIdx - 1] !== null && row[colIdx - 1] !== '' ? row[colIdx - 1] : '' }}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          
                          <div class="step-actions">
                            <button 
                              type="button" 
                              class="btn-next-step"
                              :disabled="analysis.selectedHeaderRowIndex === null"
                              @click="confirmHeaderRow(idx)"
                            >
                              다음: 적요 선택
                              <v-icon size="16">mdi-arrow-right</v-icon>
                            </button>
                          </div>
                        </div>
                        
                        <!-- ===== STEP 2: 적요 컬럼 선택 ===== -->
                        <div v-else-if="analysis.step === 'memo'" class="memo-selection-step">
                          <div class="step-title">
                            <span class="step-badge">2</span>
                            <span>적요 컬럼 선택</span>
                            <button type="button" class="btn-back-step" @click="analysis.step = 'header'">
                              <v-icon size="14">mdi-arrow-left</v-icon>
                              헤더 다시 선택
                            </button>
                          </div>
                          
                          <!-- 컬럼 매핑 정보 -->
                          <div class="mapping-info">
                            <div class="mapping-item role-date" :class="{ missing: analysis.columnMapping.date === undefined }">
                              <span class="mapping-label">날짜</span>
                              <span class="mapping-value">
                                {{ analysis.columnMapping.date !== undefined ? analysis.headers[analysis.columnMapping.date] : '미감지' }}
                              </span>
                            </div>
                            <div class="mapping-item role-income" :class="{ missing: analysis.columnMapping.income === undefined && analysis.columnMapping.amount === undefined }">
                              <span class="mapping-label">입금</span>
                              <span class="mapping-value">
                                {{ analysis.columnMapping.income !== undefined ? analysis.headers[analysis.columnMapping.income] : (analysis.columnMapping.amount !== undefined ? '(금액)' : '미감지') }}
                              </span>
                            </div>
                            <div class="mapping-item role-expense" :class="{ missing: analysis.columnMapping.expense === undefined && analysis.columnMapping.amount === undefined }">
                              <span class="mapping-label">출금</span>
                              <span class="mapping-value">
                                {{ analysis.columnMapping.expense !== undefined ? analysis.headers[analysis.columnMapping.expense] : (analysis.columnMapping.amount !== undefined ? '(금액)' : '미감지') }}
                              </span>
                            </div>
                            <div class="mapping-item role-balance">
                              <span class="mapping-label">잔액</span>
                              <span class="mapping-value">
                                {{ analysis.columnMapping.balance !== undefined ? analysis.headers[analysis.columnMapping.balance] : '-' }}
                              </span>
                            </div>
                          </div>
                          
                          <!-- 적요 컬럼 선택 -->
                          <div class="memo-column-select">
                            <div class="memo-select-label">
                              <v-icon size="14" color="#1565c0">mdi-text-box-outline</v-icon>
                              <span>적요로 사용할 컬럼 선택 (복수 선택 가능)</span>
                            </div>
                            <div class="memo-columns">
                              <label 
                                v-for="(header, hIdx) in analysis.headers" 
                                :key="hIdx"
                                class="memo-col-checkbox"
                                :class="{ 
                                  checked: analysis.selectedMemoColumns.includes(hIdx),
                                  disabled: isReservedColumn(analysis, hIdx)
                                }"
                              >
                                <input 
                                  type="checkbox" 
                                  :checked="analysis.selectedMemoColumns.includes(hIdx)"
                                  :disabled="isReservedColumn(analysis, hIdx)"
                                  @change="toggleQuickMemoColumn(idx, hIdx)"
                                />
                                <span class="col-name">{{ header || `컬럼${hIdx + 1}` }}</span>
                              </label>
                            </div>
                          </div>
                          
                          <!-- 샘플 데이터 미리보기 -->
                          <div class="sample-preview">
                            <div class="preview-label-row">
                              <span class="preview-label">샘플 데이터 (상위 3행)</span>
                              <div class="preview-legend" aria-hidden="true">
                                <span class="leg col-date">날짜</span>
                                <span class="leg col-income">입금</span>
                                <span class="leg col-expense">출금</span>
                                <span class="leg col-balance">잔액</span>
                                <span class="leg col-memo">적요(선택)</span>
                              </div>
                            </div>
                            <div class="preview-table-wrapper">
                              <table class="preview-table role-colored">
                                <thead>
                                  <tr>
                                    <th v-for="(header, hIdx) in analysis.headers" :key="hIdx" 
                                        :class="quickSampleColClass(analysis, hIdx)">
                                      <span class="th-name">{{ header || `컬럼${hIdx + 1}` }}</span>
                                      <em v-if="quickSampleRoleLabel(analysis, hIdx)" class="th-role">{{ quickSampleRoleLabel(analysis, hIdx) }}</em>
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr v-for="(row, rIdx) in analysis.sampleData" :key="rIdx">
                                    <td v-for="(cell, cIdx) in row" :key="cIdx"
                                        :class="quickSampleColClass(analysis, cIdx)">
                                      {{ cell }}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                        
                      </div>
                    </div>
                  </div>
                  
                  <!-- 업로드 버튼 -->
                  <div class="quick-upload-actions">
                    <div class="upload-summary">
                      <span v-if="validAnalysesCount === 0" class="no-files">유효한 파일이 없습니다</span>
                      <span v-else class="has-files">
                        <v-icon size="14" :color="invalidAnalysesCount > 0 ? '#f59e0b' : '#10b981'">
                          {{ invalidAnalysesCount > 0 ? 'mdi-alert' : 'mdi-check-circle' }}
                        </v-icon>
                        {{ validAnalysesCount }}개 파일 준비됨
                        <span v-if="invalidAnalysesCount > 0" class="invalid-count">({{ invalidAnalysesCount }}개 오류)</span>
                      </span>
                    </div>
                    <button 
                      type="button" 
                      class="btn-quick-upload"
                      :disabled="validAnalysesCount === 0 || isQuickUploading"
                      @click="processQuickUploadWithMapping"
                    >
                      <v-progress-circular v-if="isQuickUploading" indeterminate size="18" width="2" color="white" />
                      <template v-else>
                        <v-icon size="18">mdi-upload-multiple</v-icon>
                        업로드 ({{ validAnalysesCount }}개)
                      </template>
                    </button>
                  </div>
                </template>
                
                <!-- 빠른 업로드 결과 -->
                <div v-if="quickUploadComplete" class="quick-upload-result" :class="{ 'has-error': quickUploadResult.errorCount > 0 }">
                  <div class="result-header">
                    <v-icon 
                      size="24" 
                      :color="quickUploadResult.errorCount > 0 ? '#f59e0b' : '#10b981'"
                    >
                      {{ quickUploadResult.errorCount > 0 ? 'mdi-alert-circle' : 'mdi-check-circle' }}
                    </v-icon>
                    <div class="result-text">
                      <p>업로드 {{ quickUploadResult.errorCount > 0 ? '부분 완료' : '완료' }}</p>
                      <span>총 {{ quickUploadResult.totalCount }}건 등록 (성공 {{ quickUploadResult.successCount }}개, 실패 {{ quickUploadResult.errorCount }}개)</span>
                    </div>
                  </div>
                  <button type="button" class="btn-close-result" @click="resetQuickUpload">
                    확인
                  </button>
                </div>
              </div>
              
              <!-- 학습 업로드 모드는 사용하지 않음 (단순 업로드만) -->
              <div v-if="false" class="learning-upload-mode">
                <div class="learning-upload-guide">
                  <v-icon size="16" color="#6366f1">mdi-school</v-icon>
                  <span>적요 컬럼을 선택하고 계정을 직접 지정하여 학습시킵니다.</span>
                </div>
                
                <!-- 통장 선택 + 파일 업로드 한 줄 -->
                <div class="upload-row">
                  <!-- 통장 선택 -->
                  <div class="bank-select-inline">
                    <label class="inline-label">
                      <v-icon size="16" color="#1565c0">mdi-bank</v-icon>
                      통장
                    </label>
                    <select v-model="selectedUploadBankCode" class="bank-select-dropdown" @change="onUploadBankSelectChange">
                      <option value="">-- 선택 --</option>
                      <option 
                        v-for="bank in activeBankList" 
                        :key="bank.mb_code"
                        :value="bank.mb_code"
                      >
                        {{ bank.mb_alias }} ({{ bank.mb_bank_name }})
                      </option>
                    </select>
                  </div>
                  <p v-if="selectedUploadBankLabel" class="upload-bank-hint">
                    <v-icon size="14" color="#1565c0">mdi-information-outline</v-icon>
                    선택된 통장: <strong>{{ selectedUploadBankLabel }}</strong>
                  </p>
                  
                  <!-- 파일 업로드 -->
                  <div class="file-select-inline">
                    <label class="inline-label">
                      <v-icon size="16" color="#1565c0">mdi-file-excel</v-icon>
                      파일
                    </label>
                    <div 
                      class="file-dropzone-inline"
                      :class="{ 'drag-over': isDragOver, 'has-file': excelFile, 'disabled': !selectedUploadBankCode }"
                      @dragover.prevent="isDragOver = true"
                      @dragleave.prevent="isDragOver = false"
                      @drop.prevent="handleDrop"
                      @click="selectedUploadBankCode && triggerFileInput()"
                    >
                      <input 
                        ref="fileInputRef"
                        type="file" 
                        accept=".xlsx,.xls,.csv"
                        @change="handleFileSelect"
                        hidden
                      />
                      <template v-if="!excelFile">
                        <span class="placeholder">{{ selectedUploadBankCode ? '클릭하여 파일 선택' : '먼저 통장 선택' }}</span>
                      </template>
                      <template v-else>
                        <v-icon size="18" color="#10b981">mdi-file-excel</v-icon>
                        <span class="file-name">{{ excelFile.name }}</span>
                        <span class="file-size">({{ formatFileSize(excelFile.size) }})</span>
                        <button type="button" class="btn-remove-inline" @click.stop="removeFile">
                          <v-icon size="14">mdi-close</v-icon>
                        </button>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- 분석 버튼 영역 (학습 모드에서만) -->
              <div v-if="false" class="analyze-buttons">
                <button 
                  type="button" 
                  class="btn-process"
                  @click="loadFilePreview"
                >
                  <v-icon size="18">mdi-table-search</v-icon>
                  헤더 행 직접 선택
                </button>
              </div>
              
              <!-- 헤더 행 선택 UI -->
              <div v-if="needHeaderSelection && headerPreviewData" class="header-selection-section">
                <!-- 시트 선택 (여러 시트가 있는 경우) -->
                <div v-if="sheetNames.length > 1" class="sheet-selection">
                  <label>시트 선택:</label>
                  <select v-model="selectedSheetIndex" @change="onSheetChange(selectedSheetIndex)">
                    <option v-for="(name, idx) in sheetNames" :key="idx" :value="idx">
                      {{ name }}
                    </option>
                  </select>
                </div>
                
                <div class="header-selection-header">
                  <v-icon size="20" color="#1565c0">mdi-table-row</v-icon>
                  <div class="header-text">
                    <h4>헤더 행 선택</h4>
                    <p>날짜, 금액 등 컬럼명이 있는 행을 클릭하세요.</p>
                  </div>
                </div>
                
                <div class="header-preview-table">
                  <table class="preview-table header-select-mode">
                    <tbody>
                      <tr 
                        v-for="(row, rowIdx) in headerPreviewData" 
                        :key="rowIdx"
                        :class="{ 'selected-header-row': selectedHeaderRowIndex === rowIdx, 'clickable': true }"
                        @click="selectLearningHeaderRow(rowIdx)"
                      >
                        <td class="td-row-num">{{ rowIdx + 1 }}</td>
                        <td v-for="(cell, cellIdx) in row.slice(0, 8)" :key="cellIdx">
                          {{ cell || '-' }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div class="header-selection-actions">
                  <button type="button" class="btn-cancel" @click="cancelHeaderSelection">취소</button>
                  <button type="button" class="btn-auto-detect" @click="processExcelData(false)">
                    <v-icon size="16">mdi-auto-fix</v-icon>
                    자동 감지
                  </button>
                  <button 
                    type="button" 
                    class="btn-confirm"
                    :disabled="selectedHeaderRowIndex === null"
                    @click="confirmHeaderSelection"
                  >
                    <v-icon size="16">mdi-check</v-icon>
                    선택 완료
                  </button>
                </div>
              </div>
          
              <!-- 적요 컬럼 선택 UI -->
              <div v-if="needMemoSelection && memoSelectionData" class="memo-selection-section">
            <div class="memo-selection-header">
              <v-icon size="20" color="#f59e0b">mdi-alert-circle</v-icon>
              <div class="header-text">
                <h4>적요 컬럼 선택</h4>
                <p>기본 컬럼을 선택하고, 특정 행만 다르게 하려면 해당 셀을 클릭하세요.</p>
              </div>
              <!-- 상단 버튼 -->
              <button 
                type="button" 
                class="btn-confirm-top"
                :disabled="!canConfirmMemoSelection"
                @click="showLearningStep"
              >
                <v-icon size="16">mdi-school</v-icon>
                선택 완료 후 학습하기
              </button>
            </div>
            
            <!-- 기본 컬럼 선택 -->
            <div class="memo-column-list">
              <span class="column-list-label">기본 컬럼:</span>
              <label 
                v-for="header in memoSelectionData.headers" 
                :key="header.index"
                class="memo-column-item"
                :class="{ selected: selectedMemoColumns.includes(header.index) }"
              >
                <input 
                  type="checkbox" 
                  :checked="selectedMemoColumns.includes(header.index)"
                  @change="toggleMemoColumn(header.index)"
                />
                <span class="column-index">{{ header.index + 1 }}</span>
                <span class="column-name">{{ header.name }}</span>
              </label>
            </div>
            
            <!-- 개별 오버라이드 안내 -->
            <div v-if="Object.keys(selectedMemoCells).length > 0" class="cell-override-hint">
              <v-icon size="14" color="#f59e0b">mdi-alert</v-icon>
              <span>{{ Object.keys(selectedMemoCells).length }}개 행이 개별 설정됨</span>
              <button type="button" class="btn-reset-override" @click="resetCellSelection">
                초기화
              </button>
            </div>
            
            <!-- 데이터 미리보기 (테이블 형태) -->
            <div class="memo-preview-table">
              <div class="preview-header">
                <v-icon size="14" color="#6366f1">mdi-eye</v-icon>
                <span>데이터 미리보기</span>
                <span class="selected-hint">(셀 클릭으로 개별 행 설정 가능)</span>
              </div>
              <div class="preview-table-wrapper">
                <table class="preview-table cell-select-mode">
                  <thead>
                    <tr>
                      <th class="th-row-num">#</th>
                      <th 
                        v-for="header in memoSelectionData.headers" 
                        :key="header.index"
                        :class="{ selected: selectedMemoColumns.includes(header.index), clickable: true }"
                        @click="toggleMemoColumn(header.index)"
                      >
                        {{ header.name }}
                      </th>
                      <th class="th-memo-result">적요 결과</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr 
                      v-for="(row, rowIdx) in memoSelectionData.sampleData" 
                      :key="rowIdx"
                      :class="{ 'row-overridden': hasRowOverride(rowIdx) }"
                    >
                      <td class="td-row-num">
                        {{ rowIdx + 1 }}
                        <v-icon v-if="hasRowOverride(rowIdx)" size="10" color="#f59e0b">mdi-pencil</v-icon>
                      </td>
                      <td 
                        v-for="header in memoSelectionData.headers" 
                        :key="header.index"
                        :class="{ selected: isCellSelected(rowIdx, header.index) }"
                        @click="toggleCellSelection(rowIdx, header.index)"
                      >
                        {{ row[header.index] || '-' }}
                      </td>
                      <td class="td-memo-result">
                        {{ getCellMemoResult(rowIdx, row) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <!-- 버튼 -->
            <div class="memo-selection-actions">
              <button type="button" class="btn-cancel" @click="cancelMemoSelection">취소</button>
              <button 
                type="button" 
                class="btn-confirm"
                :disabled="!canConfirmMemoSelection"
                @click="showLearningStep"
              >
                <v-icon size="16">mdi-school</v-icon>
                선택 완료 후 학습하기
              </button>
            </div>
          </div>

          <!-- 진행 상황 표시 -->
          <div v-if="isProcessing || processComplete" class="process-status">
            <div class="process-header">
              <v-icon size="18" :color="processComplete ? '#10b981' : '#6366f1'">
                {{ processComplete ? 'mdi-check-circle' : 'mdi-loading mdi-spin' }}
              </v-icon>
              <span>{{ processStatus.message }}</span>
            </div>

            <!-- 진행바 -->
            <div v-if="(processStatus.total ?? 0) > 0" class="process-progress">
              <div class="progress-bar">
                <div 
                  class="progress-fill"
                  :style="{ width: `${((processStatus.current || 0) / (processStatus.total || 1)) * 100}%` }"
                ></div>
              </div>
            </div>

            <!-- 완료 결과 -->
            <div v-if="processComplete" class="process-result compact">
              <div class="result-row">
                <div class="result-item income">
                  <v-icon size="16" color="#10b981">mdi-plus-circle</v-icon>
                  <span>수입 {{ processResult.incomeCount }}건</span>
                  <span class="amount">{{ formatNumber(processResult.incomeTotal) }}원</span>
                </div>
                <div class="result-item expense">
                  <v-icon size="16" color="#f97316">mdi-minus-circle</v-icon>
                  <span>지출 {{ processResult.expenseCount }}건</span>
                  <span class="amount">{{ formatNumber(processResult.expenseTotal) }}원</span>
                </div>
              </div>
              <button type="button" class="btn-close-result" @click="closeDialog">
                <v-icon size="16">mdi-check</v-icon>
                완료
              </button>
            </div>
          </div>
          </template>
        </div>
      </div>

      <!-- 학습 단계 UI 비활성 -->
      <div v-if="false" class="learning-fullscreen">
        <!-- 상단 툴바 -->
        <div class="learning-toolbar">
          <div class="toolbar-left">
            <button type="button" class="btn-back" @click="backToMemoSelection">
              <v-icon size="18">mdi-arrow-left</v-icon>
              이전
            </button>
            <div class="file-badge">
              <v-icon size="14">mdi-file-excel</v-icon>
              {{ excelFile?.name }}
            </div>
          </div>
          <div class="toolbar-center">
            <!-- 학습 진행 안내 -->
            <span class="learning-hint">적요별로 계정을 지정하세요</span>
          </div>
          <div class="toolbar-right">
            <div class="progress-badge">
              <span class="done">{{ learnedGroupCount }}</span>
              <span class="divider">/</span>
              <span class="total">{{ memoGroups.length }}</span>
              <span class="label">학습됨</span>
            </div>
          </div>
        </div>

        <!-- 메인 컨텐츠 (2분할) -->
        <div class="learning-content">
          <!-- 좌측: 그룹 목록 -->
          <div class="groups-panel">
            <div class="panel-header">
              <h4>적요 그룹</h4>
              <div class="header-controls">
                <div class="sort-controls">
                  <select v-model="groupSortField" class="sort-select">
                    <option value="date">날짜순</option>
                    <option value="amount">금액순</option>
                  </select>
                  <button 
                    type="button" 
                    class="sort-order-btn"
                    @click="groupSortOrder = groupSortOrder === 'asc' ? 'desc' : 'asc'"
                    :title="groupSortOrder === 'asc' ? '오름차순' : '내림차순'"
                  >
                    <v-icon size="14">{{ groupSortOrder === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending' }}</v-icon>
                  </button>
                </div>
                <div class="filter-tabs">
                  <button 
                    :class="{ active: groupFilter === 'all' }"
                    @click="groupFilter = 'all'"
                  >전체</button>
                  <button 
                    :class="{ active: groupFilter === 'unlearned' }"
                    @click="groupFilter = 'unlearned'"
                  >미학습</button>
                  <button 
                    :class="{ active: groupFilter === 'learned' }"
                    @click="groupFilter = 'learned'"
                  >학습됨</button>
                </div>
              </div>
            </div>
            
            <!-- 검색 영역 -->
            <div class="search-section">
              <div class="search-input-wrapper">
                <v-icon size="16" color="#94a3b8">mdi-magnify</v-icon>
                <input 
                  type="text" 
                  v-model="groupSearchQuery"
                  placeholder="적요 검색 (예: 급여, 회식)"
                  class="search-input"
                  @keyup.enter="applyGroupSearch"
                />
                <button 
                  v-if="groupSearchQuery" 
                  type="button" 
                  class="search-clear-btn"
                  @click="clearGroupSearch"
                >
                  <v-icon size="14">mdi-close</v-icon>
                </button>
              </div>
              <!-- 검색 결과 일괄 처리 -->
              <div v-if="searchResultGroups.length > 0 && groupSearchQuery" class="search-result-actions">
                <div class="search-result-info">
                  <v-icon size="14" color="#6366f1">mdi-filter-variant</v-icon>
                  <span><strong>{{ searchResultGroups.length }}</strong>개 그룹 검색됨</span>
                </div>
                <button 
                  type="button" 
                  class="btn-bulk-assign"
                  @click="openBulkAssignModal"
                  :disabled="searchResultGroups.length === 0"
                >
                  <v-icon size="14">mdi-tag-multiple</v-icon>
                  일괄 계정 지정
                </button>
              </div>
              <!-- 다중 선택 모드 -->
              <div class="multi-select-toolbar">
                <label class="multi-select-toggle">
                  <input type="checkbox" v-model="isMultiSelectMode" @change="onMultiSelectModeChange">
                  <span>다중 선택</span>
                </label>
                <template v-if="isMultiSelectMode">
                  <span class="selected-count" v-if="selectedGroupIndices.size > 0">
                    {{ selectedGroupIndices.size }}개 선택됨
                  </span>
                  <button 
                    type="button" 
                    class="btn-bulk-assign-selected"
                    @click="openBulkAssignSelectedModal"
                    :disabled="selectedGroupIndices.size === 0"
                  >
                    <v-icon size="14">mdi-tag-multiple</v-icon>
                    선택 항목 계정 지정
                  </button>
                  <button 
                    type="button" 
                    class="btn-select-all"
                    @click="toggleSelectAll"
                  >
                    {{ isAllDisplayedSelected ? '전체 해제' : '전체 선택' }}
                  </button>
                </template>
              </div>
            </div>
            
            <div class="groups-list">
              <div 
                v-for="(group, idx) in displayedGroups" 
                :key="group.keyword + '-' + group.gubun"
                class="group-card"
                :class="{ 
                  selected: selectedGroupIdx === getOriginalIndex(group),
                  learned: group.learned || group.selectedScode,
                  income: group.gubun === 'IN',
                  expense: group.gubun === 'OUT',
                  'search-match': groupSearchQuery && isSearchMatch(group),
                  'multi-selected': isMultiSelectMode && selectedGroupIndices.has(getOriginalIndex(group)),
                  'multi-select-mode': isMultiSelectMode
                }"
                @click="onGroupCardClick(group, $event)"
              >
                <!-- 다중 선택 체크박스 (왼쪽) -->
                <div v-if="isMultiSelectMode" class="card-checkbox" @click.stop>
                  <input 
                    type="checkbox" 
                    :checked="selectedGroupIndices.has(getOriginalIndex(group))"
                    @change="toggleGroupSelection(getOriginalIndex(group))"
                  >
                </div>
                <!-- 카드 콘텐츠 -->
                <div class="card-content">
                  <div class="card-top">
                    <span class="gubun-badge" :class="group.gubun">
                      {{ group.gubun === 'IN' ? '수입' : '지출' }}
                    </span>
                    <span v-if="getGroupDateDisplay(group)" class="date-badge">{{ getGroupDateDisplay(group) }}</span>
                    <span class="count-badge">{{ group.count }}건</span>
                    <span class="total-badge">{{ formatNumber(group.totalPrice) }}원</span>
                  </div>
                  <div class="card-keyword">{{ group.keyword }}</div>
                  <div class="card-bottom">
                    <template v-if="group.learned || group.selectedScode">
                      <v-icon size="12" color="#10b981">mdi-check-circle</v-icon>
                      <span class="scode-name">{{ getScodeNameForGroup(group) }}</span>
                    </template>
                    <template v-else>
                      <span class="unlearned-hint">계정 미지정</span>
                    </template>
                  </div>
                </div>
              </div>
              
              <!-- 빈 상태 -->
              <div v-if="displayedGroups.length === 0" class="empty-state">
                <v-icon size="40" color="#cbd5e1">{{ groupSearchQuery ? 'mdi-magnify-close' : 'mdi-check-all' }}</v-icon>
                <p>{{ getEmptyStateMessage }}</p>
              </div>
            </div>
          </div>
          
          <!-- 일괄 계정 지정 모달 -->
          <v-dialog v-model="showBulkAssignModal" max-width="500">
            <div class="bulk-assign-modal">
              <div class="bulk-modal-header">
                <v-icon size="24" color="#6366f1">mdi-tag-multiple</v-icon>
                <div class="header-text">
                  <h3>일괄 계정 지정</h3>
                  <p>"{{ groupSearchQuery }}" 검색 결과 {{ searchResultGroups.length }}개 그룹</p>
                </div>
                <button class="close-btn" @click="showBulkAssignModal = false">
                  <v-icon size="20">mdi-close</v-icon>
                </button>
              </div>
              
              <div class="bulk-modal-body">
                <!-- 검색된 그룹 미리보기 -->
                <div class="bulk-preview">
                  <div class="preview-header">
                    <span>포함된 적요 그룹</span>
                    <span class="count">{{ searchResultGroups.length }}개</span>
                  </div>
                  <div class="preview-list">
                    <div 
                      v-for="(group, idx) in searchResultGroups.slice(0, 5)" 
                      :key="idx"
                      class="preview-item"
                      :class="group.gubun"
                    >
                      <span class="gubun-badge">{{ group.gubun === 'IN' ? '수입' : '지출' }}</span>
                      <span class="keyword">{{ group.keyword }}</span>
                      <span class="count">{{ group.count }}건</span>
                    </div>
                    <div v-if="searchResultGroups.length > 5" class="preview-more">
                      ... 외 {{ searchResultGroups.length - 5 }}개
                    </div>
                  </div>
                </div>
                
                <!-- 구분 선택 -->
                <div class="bulk-gubun-select">
                  <label>적용 대상</label>
                  <div class="gubun-options">
                    <label class="gubun-option" :class="{ active: bulkAssignGubun === 'all' }">
                      <input type="radio" v-model="bulkAssignGubun" value="all" />
                      <span>전체 ({{ searchResultGroups.length }})</span>
                    </label>
                    <label class="gubun-option income" :class="{ active: bulkAssignGubun === 'IN' }">
                      <input type="radio" v-model="bulkAssignGubun" value="IN" />
                      <span>수입만 ({{ searchResultGroups.filter(g => g.gubun === 'IN').length }})</span>
                    </label>
                    <label class="gubun-option expense" :class="{ active: bulkAssignGubun === 'OUT' }">
                      <input type="radio" v-model="bulkAssignGubun" value="OUT" />
                      <span>지출만 ({{ searchResultGroups.filter(g => g.gubun === 'OUT').length }})</span>
                    </label>
                  </div>
                </div>
                
                <!-- 계정 선택 -->
                <div class="bulk-account-select">
                  <label>지정할 계정</label>
                  <v-autocomplete
                    v-model="bulkAssignScode"
                    :items="bulkAssignGubun === 'IN' ? incomeCodeList : (bulkAssignGubun === 'OUT' ? expenseCodeList : [...incomeCodeList, ...expenseCodeList])"
                    item-title="sname"
                    item-value="scode"
                    variant="outlined"
                    density="comfortable"
                    hide-details
                    placeholder="계정을 선택하세요"
                    class="account-autocomplete"
                  />
                </div>
                
                <!-- 키워드로 등록 옵션 -->
                <div class="bulk-keyword-option">
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="bulkRegisterAsKeyword" />
                    <span>검색어 "{{ groupSearchQuery }}"를 키워드로도 등록</span>
                  </label>
                  <p class="option-hint">체크하면 다음 업로드 시 "{{ groupSearchQuery }}"가 포함된 적요에 자동으로 이 계정이 추천됩니다.</p>
                </div>
              </div>
              
              <div class="bulk-modal-footer">
                <button type="button" class="btn-cancel" @click="showBulkAssignModal = false">취소</button>
                <button 
                  type="button" 
                  class="btn-apply"
                  :disabled="!bulkAssignScode"
                  @click="applyBulkAssign"
                >
                  <v-icon size="16">mdi-check</v-icon>
                  {{ getBulkApplyCount }}개 그룹에 적용
                </button>
              </div>
            </div>
          </v-dialog>
          
          <!-- 다중 선택 일괄 계정 지정 모달 -->
          <v-dialog v-model="showBulkAssignSelectedModal" max-width="500">
            <div class="bulk-assign-modal">
              <div class="bulk-modal-header">
                <v-icon size="24" color="#6366f1">mdi-tag-multiple</v-icon>
                <div class="header-text">
                  <h3>선택 항목 계정 지정</h3>
                  <p>{{ selectedGroupIndices.size }}개 그룹 선택됨</p>
                </div>
                <button class="close-btn" @click="showBulkAssignSelectedModal = false">
                  <v-icon size="20">mdi-close</v-icon>
                </button>
              </div>
              
              <div class="bulk-modal-body">
                <!-- 선택된 그룹 미리보기 -->
                <div class="bulk-preview">
                  <div class="preview-header">
                    <span>선택된 적요 그룹</span>
                    <span class="count">{{ selectedGroupIndices.size }}개</span>
                  </div>
                  <div class="preview-list">
                    <div 
                      v-for="(group, idx) in selectedGroupsForBulk.slice(0, 5)" 
                      :key="idx"
                      class="preview-item"
                      :class="group.gubun"
                    >
                      <span class="gubun-badge">{{ group.gubun === 'IN' ? '수입' : '지출' }}</span>
                      <span class="keyword">{{ group.keyword }}</span>
                    </div>
                    <div v-if="selectedGroupsForBulk.length > 5" class="preview-more">
                      ... 외 {{ selectedGroupsForBulk.length - 5 }}개
                    </div>
                  </div>
                </div>
                
                <!-- 구분 선택 -->
                <div class="bulk-gubun-select">
                  <label>적용 대상</label>
                  <div class="gubun-options">
                    <label class="gubun-option" :class="{ active: bulkSelectedGubun === 'all' }">
                      <input type="radio" v-model="bulkSelectedGubun" value="all" />
                      <span>전체 ({{ selectedGroupsForBulk.length }})</span>
                    </label>
                    <label class="gubun-option income" :class="{ active: bulkSelectedGubun === 'IN' }">
                      <input type="radio" v-model="bulkSelectedGubun" value="IN" />
                      <span>수입만 ({{ selectedGroupsForBulk.filter(g => g.gubun === 'IN').length }})</span>
                    </label>
                    <label class="gubun-option expense" :class="{ active: bulkSelectedGubun === 'OUT' }">
                      <input type="radio" v-model="bulkSelectedGubun" value="OUT" />
                      <span>지출만 ({{ selectedGroupsForBulk.filter(g => g.gubun === 'OUT').length }})</span>
                    </label>
                  </div>
                </div>
                
                <!-- 계정 선택 -->
                <div class="bulk-account-select">
                  <label>지정할 계정</label>
                  <v-autocomplete
                    v-model="bulkSelectedScode"
                    :items="bulkSelectedGubun === 'IN' ? incomeCodeList : (bulkSelectedGubun === 'OUT' ? expenseCodeList : [...incomeCodeList, ...expenseCodeList])"
                    item-title="sname"
                    item-value="scode"
                    variant="outlined"
                    density="comfortable"
                    hide-details
                    placeholder="계정을 선택하세요"
                    class="account-autocomplete"
                  />
                </div>
              </div>
              
              <div class="bulk-modal-footer">
                <button type="button" class="btn-cancel" @click="showBulkAssignSelectedModal = false">취소</button>
                <button 
                  type="button" 
                  class="btn-apply"
                  :disabled="!bulkSelectedScode"
                  @click="applyBulkAssignSelected"
                >
                  <v-icon size="16">mdi-check</v-icon>
                  {{ getBulkSelectedApplyCount }}개 그룹에 적용
                </button>
              </div>
            </div>
          </v-dialog>

          <!-- 우측: 상세 패널 -->
          <div class="detail-panel">
            <template v-if="selectedGroup">
              <!-- 선택된 그룹 정보 -->
              <div class="detail-header">
                <div class="keyword-display">
                  <span class="gubun-tag" :class="selectedGroup.gubun">
                    {{ selectedGroup.gubun === 'IN' ? '수입' : '지출' }}
                  </span>
                  <h3>{{ selectedGroup.keyword }}</h3>
                </div>
                <div class="detail-stats">
                  <span>{{ selectedGroup.count }}건</span>
                  <span>총 {{ formatNumber(selectedGroup.totalPrice) }}원</span>
                </div>
              </div>

              <!-- 계정 선택 -->
              <div class="account-select-section">
                <label>계정 선택</label>
                <v-autocomplete
                  v-model="selectedGroup.selectedScode"
                  :items="selectedGroup.gubun === 'IN' ? incomeCodeList : expenseCodeList"
                  item-title="sname"
                  item-value="scode"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  placeholder="계정을 선택하세요"
                  class="account-autocomplete"
                  @update:modelValue="selectedGroupIdx !== null && onScodeSelect(selectedGroupIdx)"
                />
                
                <!-- 학습 출처 표시 -->
                <div v-if="selectedGroup.learned" class="learned-source">
                  <v-icon size="14" color="#10b981">mdi-school</v-icon>
                  <span>{{ getLearningSourceLabel(selectedGroup.learned.source) }}에서 학습됨</span>
                </div>
              </div>

              <!-- 해당 키워드에 포함되는 적요 목록 -->
              <div class="matching-memos">
                <div class="memos-header">
                  <h4>포함된 적요 ({{ selectedGroup.memos.length }}건)</h4>
                </div>
                <div class="memos-list">
                  <div 
                    v-for="(memo, mCode) in sortedSelectedMemos" 
                    :key="mCode"
                    class="memo-item"
                  >
                    <span class="memo-date" v-if="memo.date">{{ formatMemoDate(memo.date) }}</span>
                    <span class="memo-text">{{ memo.memo }}</span>
                    <span class="memo-price">{{ formatNumber(memo.price) }}원</span>
                  </div>
                </div>
              </div>
              
              <!-- 등록된 키워드 목록 (검색으로 등록된 것) -->
              <div v-if="registeredKeywords.length > 0" class="registered-keywords-section">
                <div class="section-header">
                  <h4>
                    <v-icon size="16">mdi-tag-multiple</v-icon>
                    이번에 등록할 키워드
                  </h4>
                </div>
                <div class="registered-list">
                  <div 
                    v-for="(rk, rkIdx) in registeredKeywords" 
                    :key="rkIdx"
                    class="registered-item"
                  >
                    <span class="rk-gubun" :class="rk.gubun">{{ rk.gubun === 'IN' ? '수입' : '지출' }}</span>
                    <span class="rk-keyword">{{ rk.keyword }}</span>
                    <v-icon size="12" color="#94a3b8">mdi-arrow-right</v-icon>
                    <span class="rk-account">{{ rk.scodeName }}</span>
                    <button type="button" class="rk-remove" @click="removeRegisteredKeyword(rkIdx)">
                      <v-icon size="12">mdi-close</v-icon>
                    </button>
                  </div>
                </div>
              </div>
            </template>
            
            <!-- 선택 안됨 -->
            <div v-else class="empty-detail">
              <v-icon size="60" color="#e2e8f0">mdi-gesture-tap</v-icon>
              <p>좌측에서 적요 그룹을 선택하세요</p>
            </div>
          </div>
        </div>

        <!-- 하단 액션 바 -->
        <div class="learning-footer">
          <div class="footer-info">
            <v-icon size="16" color="#6366f1">mdi-information</v-icon>
            <span>학습된 키워드는 다음 업로드 시 자동으로 계정이 추론됩니다.</span>
          </div>
          <div class="footer-actions">
            <button type="button" class="btn-skip" @click="showConfirmDialog('skip')">
              학습 없이 등록만
            </button>
            <button 
              type="button" 
              class="btn-save-register"
              :disabled="isProcessing"
              @click="showConfirmDialog('saveAndRegister')"
            >
              <v-progress-circular v-if="isProcessing" indeterminate size="18" width="2" color="white" />
              <template v-else>
                <v-icon size="18">mdi-check-all</v-icon>
                학습 저장 및 등록
              </template>
            </button>
          </div>
        </div>
      </div>
      
      <!-- 확인 다이얼로그 -->
      <v-dialog v-model="confirmDialogOpen" max-width="420" persistent>
        <div class="confirm-dialog">
          <div class="confirm-dialog__header">
            <span>{{ confirmDialogType === 'skip' ? '학습 없이 등록' : '학습 저장 및 등록' }}</span>
            <button type="button" class="confirm-dialog__close" :disabled="isProcessing" aria-label="닫기" @click="confirmDialogOpen = false">
              <v-icon size="18">mdi-close</v-icon>
            </button>
          </div>
          <div class="confirm-icon" :class="confirmDialogType">
            <v-icon size="32" :color="confirmDialogType === 'skip' ? '#f59e0b' : '#6366f1'">
              {{ confirmDialogType === 'skip' ? 'mdi-alert-circle' : 'mdi-school' }}
            </v-icon>
          </div>
          <div class="confirm-content">
            <h3>{{ confirmDialogType === 'skip' ? '학습 없이 등록하시겠습니까?' : '학습 저장 및 등록하시겠습니까?' }}</h3>
            <p v-if="confirmDialogType === 'skip'">
              적요 그룹에 지정한 계정은 저장되지 않습니다.<br>
              다음 업로드 시 다시 계정을 지정해야 합니다.
            </p>
            <p v-else>
              <strong>{{ learnedGroupCount }}개 그룹</strong>의 계정 정보가 학습됩니다.<br>
              <template v-if="registeredKeywords.length > 0">
                <strong>{{ registeredKeywords.length }}개 키워드</strong>가 새로 등록됩니다.<br>
              </template>
              다음 업로드 시 자동으로 계정이 추론됩니다.
            </p>
          </div>
          <div class="confirm-summary" v-if="confirmDialogType === 'saveAndRegister'">
            <div class="summary-item">
              <v-icon size="16" color="#10b981">mdi-check-circle</v-icon>
              <span>학습된 그룹: {{ learnedGroupCount }}개</span>
            </div>
            <div class="summary-item" v-if="registeredKeywords.length > 0">
              <v-icon size="16" color="#f59e0b">mdi-tag-multiple</v-icon>
              <span>등록할 키워드: {{ registeredKeywords.length }}개</span>
            </div>
            <div class="summary-item">
              <v-icon size="16" color="#6366f1">mdi-file-document</v-icon>
              <span>등록할 데이터: {{ getTotalMemoCount }}건</span>
            </div>
          </div>
          <div class="confirm-actions">
            <button type="button" class="btn-cancel" @click="confirmDialogOpen = false">
              취소
            </button>
            <button 
              type="button" 
              class="btn-confirm"
              :class="confirmDialogType"
              @click="executeConfirmedAction"
            >
              <v-icon size="16">{{ confirmDialogType === 'skip' ? 'mdi-check' : 'mdi-school' }}</v-icon>
              {{ confirmDialogType === 'skip' ? '등록만 진행' : '학습 및 등록' }}
            </button>
          </div>
        </div>
      </v-dialog>

      <!-- 푸터 (직접 입력 탭) -->
      <div v-if="activeTab === 'input'" class="modal-footer">
        <button type="button" class="btn-cancel" @click="closeDialog">취소</button>
        <button 
          type="button" 
          class="btn-save" 
          @click="saveManualInput"
          :disabled="!canSave || isSaving"
        >
          <v-progress-circular v-if="isSaving" indeterminate size="18" width="2" color="white" />
          <span v-else>저장</span>
        </button>
      </div>
    </div>
    
    <!-- 사용 설명서 다이얼로그 -->
    <v-dialog v-model="showHelpDialog" max-width="700">
      <div class="help-dialog">
        <div class="help-header">
          <v-icon size="24" color="#6366f1">mdi-book-open-variant</v-icon>
          <h3>통장내역 업로드 사용 설명서</h3>
          <button class="close-btn" @click="showHelpDialog = false">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>
        
        <div class="help-content">
          <div class="help-section">
            <div class="section-title">
              <span class="step-badge">1</span>
              <h4>엑셀 업로드</h4>
            </div>
            <div class="section-body">
              <p>통장별로 은행에서 받은 거래내역 파일(.xlsx / .xls / .csv)을 선택합니다.</p>
              <ul>
                <li>통장마다 파일을 고른 뒤 <strong>다음</strong>으로 헤더·적요 컬럼을 확인합니다.</li>
                <li>날짜·입금·출금(또는 금액) 컬럼은 가능한 범위에서 자동으로 잡습니다.</li>
                <li>업로드 후 계정은 <strong>통장내역</strong> 화면에서 지정합니다.</li>
              </ul>
            </div>
          </div>

          <div class="help-section">
            <div class="section-title">
              <span class="step-badge">2</span>
              <h4>직접 입력</h4>
            </div>
            <div class="section-body">
              <p>한 건씩 수입/지출·통장·일자·금액·적요를 입력해 저장합니다.</p>
              <ul>
                <li>지출이면 상대계정(선택)을 넣을 수 있습니다.</li>
                <li>같은 날 기존 건이 있으면 삽입 위치를 고를 수 있습니다.</li>
              </ul>
            </div>
          </div>

          <div class="help-section">
            <div class="section-title">
              <v-icon size="18" color="#6366f1">mdi-information-outline</v-icon>
              <h4>참고</h4>
            </div>
            <div class="section-body">
              <ul>
                <li>같은 파일을 다시 올려도 이미 등록된 행은 건너뛸 수 있습니다.</li>
                <li>마감된 통장에는 업로드할 수 없습니다.</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div class="help-footer">
          <button type="button" class="btn-close" @click="showHelpDialog = false">
            확인
          </button>
        </div>
      </div>
    </v-dialog>
    
    <!-- 저장 완료 스낵바 -->
    <v-snackbar
      v-model="showSaveSnackbar"
      :timeout="2000"
      color="#10b981"
      location="top"
    >
      <div class="save-snackbar-content">
        <v-icon size="18" class="mr-2">mdi-check-circle</v-icon>
        {{ saveSnackbarMessage }}
      </div>
    </v-snackbar>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import YmdDateInput from '@/components/common/YmdDateInput.vue';
import { bankbookApi } from '@/services/api';

// ============================================
// Props & Emits
// ============================================

interface AccountCode {
  scode: string;
  sname: string;
  gubun: 'IN' | 'OUT';
}

interface BankData {
  mb_idx: number;
  m_code: number;
  mb_code: string;
  mb_alias: string;
  mb_bank_name: string;
  mb_account_no: string;
  mb_purpose?: string;
  mb_sort_order: number;
  mb_is_active: number;
  mb_start_date?: string | null;
  mb_end_date?: string | null;
  mb_biz_gubun?: string | null;
}

interface MemoGroup {
  keyword: string;
  gubun: 'IN' | 'OUT';
  memos: Array<{ memo: string; gubun: string; price: number; date?: string }>;
  totalPrice: number;
  count: number;
  latestDate?: string;
  learned?: {
    scode: string;
    scode_name?: string;
    count: number;
    source: 'facility' | 'otherFacility' | 'allBizGubun';
  };
  selectedScode?: string;
  isEditing?: boolean;
  customKeywords?: string[];
}

const props = defineProps<{
  modelValue: boolean;
  memberCode: string | number;
  currentYear: string;
  currentMonth: string;
  bizGubun?: string;
  incomeCodeList?: AccountCode[];
  expenseCodeList?: AccountCode[];
  bankList?: BankData[];
  selectedBankCode?: string | null;
}>();

// 업로드 결과 타입
interface UploadResultInfo {
  bankCode: string;
  bankAlias: string;
  fileName: string;
  count: number;
  success: boolean;
  message?: string;
}

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'saved', items: any[]): void;
  (e: 'uploadComplete', results: UploadResultInfo[]): void;
}>();

// ============================================
// 상태
// ============================================

const dialogOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const activeTab = ref<'input' | 'excel'>('excel');
const currentStep = ref<'upload' | 'learning'>('upload');
const formRef = ref<any>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);

// 직접 입력 폼
const formData = ref({
  gubun: 'OUT' as 'IN' | 'OUT',
  inout_date: '',
  price: 0,
  memo: '',
  relAcc: '',
  bankCode: ''
});
const priceFocused = ref(false);
const isPredicting = ref(false);
const isSaving = ref(false);
const prediction = ref<any>(null);
const predictionError = ref('');

// 삽입 위치 관련
interface ExistingItem {
  cb_idx: number;
  cb_order: number;
  cb_memo: string;
  cb_gubun: string;
  cb_price: number;
}
const existingItemsForDate = ref<ExistingItem[]>([]);
const isLoadingExistingItems = ref(false);
const insertPosition = ref<'first' | 'last' | number>('last');  // 'first', 'last', 또는 cb_idx (해당 항목 뒤에 삽입)

// 엑셀 업로드
const excelFile = ref<File | null>(null);
const isDragOver = ref(false);
const isProcessing = ref(false);
const processComplete = ref(false);
const processStatus = ref<{
  message: string;
  current?: number;
  total?: number;
}>({ message: '' });

// 업로드 모드
const uploadMode = ref<'quick' | 'learning'>('quick');

// 빠른 업로드 관련
const quickUploadFiles = ref<Record<string, File>>({});
const quickFileInputRefs = ref<Record<string, HTMLInputElement | null>>({});
const isQuickUploading = ref(false);
const quickUploadComplete = ref(false);
const quickUploadResult = ref({ totalCount: 0, successCount: 0, errorCount: 0 });

// 빠른 업로드 헤더 확인 단계
const quickUploadStep = ref<'select' | 'confirm' | 'uploading'>('select');
interface QuickFileAnalysis {
  bankCode: string;
  bankAlias: string;
  file: File;
  headers: string[];
  headerRowIndex: number;
  sampleData: string[][];
  columnMapping: {
    date?: number;
    income?: number;
    expense?: number;
    amount?: number;
    balance?: number;
    memo?: number | number[];
  };
  isValid: boolean;
  errorMessage?: string;
  // 사용자 수정 가능
  selectedMemoColumns: number[];
  // 2단계 UI용
  step: 'header' | 'memo';
  previewRows: string[][];
  suggestedHeaderRowIndex: number | null;
  selectedHeaderRowIndex: number | null;
  detectionConfidence: 'high' | 'medium' | 'low';  // 헤더 감지 신뢰도
}
const quickFileAnalyses = ref<QuickFileAnalysis[]>([]);
const isAnalyzingFiles = ref(false);
const currentAnalysisIndex = ref(0);

// 다중 파일 업로드 관련
interface UploadFileItem {
  file: File;
  bankCode: string;
  status: 'pending' | 'processing' | 'done' | 'error';
  resultCount?: number;
  errorMessage?: string;
}
const uploadFileList = ref<UploadFileItem[]>([]);
const multiFileInputRef = ref<HTMLInputElement | null>(null);

const processResult = ref({
  incomeCount: 0,
  incomeTotal: 0,
  expenseCount: 0,
  expenseTotal: 0,
  totalCount: 0
});

// 헤더 행 선택
const needHeaderSelection = ref(false);
const headerPreviewData = ref<string[][] | null>(null);
const selectedHeaderRowIndex = ref<number | null>(null);
const isLoadingPreview = ref(false);

// 시트 선택
const sheetNames = ref<string[]>([]);
const selectedSheetIndex = ref(0);

// 적요 컬럼 선택
const needMemoSelection = ref(false);
const memoSelectionData = ref<{
  headers: { index: number; name: string }[];
  sampleData: string[][];
  headerRowIndex: number;
  columnMapping: any;
  autoMemoIndex?: number;
} | null>(null);
const selectedMemoColumns = ref<number[]>([]);
const selectedMemoCells = ref<Record<number, Record<number, boolean>>>({});

// 학습 단계
const showLearning = ref(false);
const learningScope = ref<'facility' | 'bizGubun' | 'all'>('facility');
const memoGroups = ref<MemoGroup[]>([]);
const isLoadingGroups = ref(false);
const selectedGroupIdx = ref<number | null>(null);
const groupFilter = ref<'all' | 'learned' | 'unlearned'>('all');
// 정렬 관련
const groupSortField = ref<'date' | 'amount'>('date');
const groupSortOrder = ref<'asc' | 'desc'>('desc');
// 검색 관련
const groupSearchQuery = ref('');
const showBulkAssignModal = ref(false);
const bulkAssignGubun = ref<'all' | 'IN' | 'OUT'>('all');
const bulkAssignScode = ref<string | null>(null);
const bulkRegisterAsKeyword = ref(true);
// 다중 선택 관련
const selectedGroupIndices = ref<Set<number>>(new Set());
const isMultiSelectMode = ref(false);
const showBulkAssignSelectedModal = ref(false);
const bulkSelectedGubun = ref<'all' | 'IN' | 'OUT'>('all');
const bulkSelectedScode = ref<string | null>(null);

// 확인 다이얼로그
const confirmDialogOpen = ref(false);
const confirmDialogType = ref<'skip' | 'saveAndRegister'>('skip');

// 직접 등록한 키워드 목록
interface RegisteredKeyword {
  gubun: 'IN' | 'OUT';
  keyword: string;
  scode: string;
  scodeName: string;
}
const registeredKeywords = ref<RegisteredKeyword[]>([]);

// 도움말
const showHelpDialog = ref(false);

// 저장 완료 스낵바
const showSaveSnackbar = ref(false);
const saveSnackbarMessage = ref('');

// 통장 선택 (업로드 모달 전용 — 사이드바 선택과 분리)
const selectedUploadBankCode = ref<string | null>(null);
const uploadBankUserTouched = ref(false);

const selectedUploadBankLabel = computed(() => {
  if (!selectedUploadBankCode.value) return '';
  const bank = activeBankList.value.find(b => b.mb_code === selectedUploadBankCode.value);
  return bank ? `${bank.mb_alias} (${bank.mb_bank_name})` : selectedUploadBankCode.value;
});

const appendUploadCommonFields = (formDataObj: FormData) => {
  formDataObj.append('year', props.currentYear);
  formDataObj.append('month', props.currentMonth);
  if (props.bizGubun) formDataObj.append('bizGubun', props.bizGubun);
};

const appendRequiredBankCode = (formDataObj: FormData, bankCode: string | null | undefined, context = '업로드') => {
  const code = String(bankCode || '').trim();
  if (!code) {
    throw new Error(`${context}: 통장을 선택해주세요.`);
  }
  formDataObj.append('bankCode', code);
  return code;
};

const onUploadBankSelectChange = () => {
  uploadBankUserTouched.value = true;
};

// ============================================
// Computed
// ============================================

const getHeaderTitle = computed(() => {
  return activeTab.value === 'input' ? '항목 추가' : '통장내역 업로드';
});

const getHeaderSubtitle = computed(() => {
  return activeTab.value === 'input'
    ? '통장·일자·금액·적요를 직접 입력합니다'
    : '통장별 엑셀 파일을 올려 내역을 등록합니다';
});

const formattedPrice = computed(() => {
  return formData.value.price ? formData.value.price.toLocaleString('ko-KR') : '';
});

const canPredict = computed(() => {
  return formData.value.memo.trim().length > 0 && formData.value.price > 0;
});

const canSave = computed(() => {
  const hasBank = activeBankList.value.length === 0 || formData.value.bankCode;
  return formData.value.inout_date && formData.value.price > 0 && formData.value.memo.trim() && hasBank;
});

// 날짜 변경 시 해당 날짜의 기존 내역 조회
const loadExistingItemsForDate = async () => {
  const date = formData.value.inout_date;
  const bankCode = formData.value.bankCode;
  
  if (!date || !props.memberCode) {
    existingItemsForDate.value = [];
    insertPosition.value = 'last';
    return;
  }
  
  isLoadingExistingItems.value = true;
  try {
    // 해당 날짜의 통장내역 조회 (startDate, endDate 동일하게 설정)
    const res = await bankbookApi.getList(
      props.memberCode, 
      date.substring(0, 4),  // year
      date.substring(5, 7),  // month
      props.bizGubun || undefined,
      bankCode || undefined
    );
    
    if (res.data?.data) {
      // 해당 날짜의 항목만 필터링하고 cb_order 순으로 정렬
      const items = res.data.data
        .filter((item: any) => item.cb_date === date)
        .sort((a: any, b: any) => (Number(a.cb_order) || 0) - (Number(b.cb_order) || 0))
        .map((item: any) => ({
          cb_idx: item.cb_idx,
          cb_order: Number(item.cb_order) || 0,
          cb_memo: item.cb_memo || '',
          cb_gubun: item.cb_gubun,
          cb_price: item.cb_price
        }));
      existingItemsForDate.value = items;
    } else {
      existingItemsForDate.value = [];
    }
  } catch (error) {
    console.error('기존 내역 조회 오류:', error);
    existingItemsForDate.value = [];
  } finally {
    isLoadingExistingItems.value = false;
    insertPosition.value = 'last';  // 기본값: 맨 뒤
  }
};

// 날짜 또는 통장 변경 시 기존 내역 조회
watch(() => [formData.value.inout_date, formData.value.bankCode], () => {
  loadExistingItemsForDate();
}, { immediate: false });

const canConfirmMemoSelection = computed(() => {
  return selectedMemoColumns.value.length > 0;
});

// 업로드 가능 여부 (통장이 있고 선택된 경우, 또는 통장 목록이 비어있는 경우)
const canUpload = computed(() => {
  if (!props.bankList || props.bankList.length === 0) return false;
  return !!selectedUploadBankCode.value;
});

// 통장이 선택 연월에 사용 가능한지 (MemberDetailBankbook.isBankActiveForCurrentMonth 와 동일)
const isBankActiveForMonth = (bank: BankData): boolean => {
  const year = String(props.currentYear);
  const month = String(props.currentMonth).padStart(2, '0');
  const currentYM = `${year}-${month}`;

  if (!bank.mb_start_date) return true;
  if (currentYM < bank.mb_start_date) return false;
  if (bank.mb_end_date && currentYM > bank.mb_end_date) return false;
  return true;
};

// 업로드 가능 통장: 사용기간 + 활성(mb_is_active=1) — 삭제(비활성) 통장 제외
const activeBankList = computed(() => {
  return (props.bankList || []).filter(
    (b) => isBankActiveForMonth(b) && b.mb_is_active === 1
  );
});

// 빠른 업로드 파일 개수
const quickUploadFileCount = computed(() => {
  return Object.keys(quickUploadFiles.value).length;
});

// 유효한 분석 파일 수
const validAnalysesCount = computed(() => {
  return quickFileAnalyses.value.filter(a => a.isValid).length;
});

// 유효하지 않은 분석 파일 수
const invalidAnalysesCount = computed(() => {
  return quickFileAnalyses.value.filter(a => !a.isValid).length;
});

// 다중 파일 업로드 가능 여부
const canMultiUpload = computed(() => {
  if (uploadFileList.value.length === 0) return false;
  // 모든 파일에 통장이 선택되어 있어야 함
  return uploadFileList.value.every(item => item.bankCode && item.status === 'pending');
});

// 업로드 진행 중인지
const isMultiUploading = computed(() => {
  return uploadFileList.value.some(item => item.status === 'processing');
});

// 모든 파일 업로드 완료 여부
const allFilesUploaded = computed(() => {
  return uploadFileList.value.length > 0 && uploadFileList.value.every(item => item.status === 'done' || item.status === 'error');
});

const learnedGroupCount = computed(() => {
  return memoGroups.value.filter(g => g.learned || g.selectedScode).length;
});

const incomeCodeList = computed(() => props.incomeCodeList || []);
const expenseCodeList = computed(() => props.expenseCodeList || []);

const filteredGroups = computed(() => {
  if (groupFilter.value === 'all') return memoGroups.value;
  if (groupFilter.value === 'learned') {
    return memoGroups.value.filter(g => g.learned || g.selectedScode);
  }
  return memoGroups.value.filter(g => !g.learned && !g.selectedScode);
});

// 검색 결과 그룹
const searchResultGroups = computed(() => {
  if (!groupSearchQuery.value.trim()) return [];
  const query = groupSearchQuery.value.toLowerCase().trim();
  return memoGroups.value.filter(g => {
    // 키워드에 검색어 포함
    if (g.keyword.toLowerCase().includes(query)) return true;
    // 포함된 적요 중 하나라도 검색어 포함
    return g.memos.some(m => m.memo.toLowerCase().includes(query));
  });
});

// 실제 표시되는 그룹 (필터 + 검색 + 정렬 적용)
const displayedGroups = computed(() => {
  let groups = [...filteredGroups.value];
  
  // 검색어가 있으면 검색 결과만 표시
  if (groupSearchQuery.value.trim()) {
    const query = groupSearchQuery.value.toLowerCase().trim();
    groups = groups.filter(g => {
      if (g.keyword.toLowerCase().includes(query)) return true;
      return g.memos.some(m => m.memo.toLowerCase().includes(query));
    });
  }
  
  // 정렬 적용
  groups.sort((a, b) => {
    let comparison = 0;
    
    if (groupSortField.value === 'date') {
      // 날짜순: latestDate 기준 (없으면 맨 뒤로)
      const dateA = a.latestDate || '';
      const dateB = b.latestDate || '';
      comparison = dateA.localeCompare(dateB);
    } else if (groupSortField.value === 'amount') {
      // 금액순: totalPrice 기준
      comparison = a.totalPrice - b.totalPrice;
    }
    
    // 정렬 순서 (오름차순/내림차순)
    return groupSortOrder.value === 'asc' ? comparison : -comparison;
  });
  
  return groups;
});

// 빈 상태 메시지
const getEmptyStateMessage = computed(() => {
  if (groupSearchQuery.value.trim()) {
    return `"${groupSearchQuery.value}" 검색 결과가 없습니다.`;
  }
  if (groupFilter.value === 'unlearned') {
    return '모든 적요가 학습되었습니다!';
  }
  return '표시할 항목이 없습니다.';
});

// 일괄 적용 대상 개수
const getBulkApplyCount = computed(() => {
  if (bulkAssignGubun.value === 'all') return searchResultGroups.value.length;
  return searchResultGroups.value.filter(g => g.gubun === bulkAssignGubun.value).length;
});

// 다중 선택된 그룹 목록
const selectedGroupsForBulk = computed(() => {
  return Array.from(selectedGroupIndices.value)
    .map(idx => memoGroups.value[idx])
    .filter(Boolean);
});

// 다중 선택 일괄 적용 대상 개수
const getBulkSelectedApplyCount = computed(() => {
  if (bulkSelectedGubun.value === 'all') return selectedGroupsForBulk.value.length;
  return selectedGroupsForBulk.value.filter(g => g.gubun === bulkSelectedGubun.value).length;
});

// 현재 표시된 그룹이 모두 선택되었는지 확인
const isAllDisplayedSelected = computed(() => {
  if (displayedGroups.value.length === 0) return false;
  return displayedGroups.value.every(group => 
    selectedGroupIndices.value.has(getOriginalIndex(group))
  );
});

// 총 적요 건수
const getTotalMemoCount = computed(() => {
  return memoGroups.value.reduce((sum, g) => sum + g.memos.length, 0);
});

const selectedGroup = computed(() => {
  if (selectedGroupIdx.value === null) return null;
  return memoGroups.value[selectedGroupIdx.value];
});

// 선택된 그룹의 적요 목록 (날짜순 정렬)
const sortedSelectedMemos = computed(() => {
  if (!selectedGroup.value) return [];
  return [...selectedGroup.value.memos].sort((a, b) => {
    // 날짜가 있으면 날짜순, 없으면 원래 순서
    if (a.date && b.date) {
      return a.date.localeCompare(b.date);
    }
    if (a.date) return -1;
    if (b.date) return 1;
    return 0;
  });
});

// ============================================
// 메서드
// ============================================

const formatNumber = (value: number): string => {
  return value.toLocaleString('ko-KR');
};

const formatMemoDate = (dateStr: string): string => {
  if (!dateStr) return '';
  // 다양한 날짜 형식 처리
  const cleaned = String(dateStr).trim();
  // YYYY-MM-DD 또는 YYYY/MM/DD 형식
  const match = cleaned.match(/(\d{4})[-/]?(\d{1,2})[-/]?(\d{1,2})/);
  if (match) {
    const month = match[2].padStart(2, '0');
    const day = match[3].padStart(2, '0');
    return `${month}/${day}`;
  }
  return cleaned;
};

const getGroupDateDisplay = (group: MemoGroup): string => {
  // 1. latestDate가 있으면 사용
  if (group.latestDate) {
    return formatMemoDate(group.latestDate);
  }
  // 2. memos에서 날짜 찾기
  if (group.memos && group.memos.length > 0) {
    const datesInMemos = group.memos
      .map(m => m.date)
      .filter(d => d)
      .sort((a, b) => (b || '').localeCompare(a || '')); // 최신순
    if (datesInMemos.length > 0) {
      return formatMemoDate(datesInMemos[0] || '');
    }
  }
  return '';
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const getConfidenceClass = (confidence: number): string => {
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.5) return 'medium';
  return 'low';
};

const getSourceName = (source: string): string => {
  switch (source) {
    case 'keyword': return '키워드 매칭';
    case 'similar': return '유사 거래';
    case 'stats': return '통계 기반';
    case 'rule': return '규칙 기반';
    default: return source || '알 수 없음';
  }
};

const formatBankAccountNo = (accountNo: string): string => {
  if (!accountNo) return '';
  if (accountNo.length <= 8) return accountNo;
  const front = accountNo.slice(0, 4);
  const back = accountNo.slice(-4);
  return `${front}****${back}`;
};

// ============================================
// 다중 파일 업로드 관련 메서드
// ============================================

const triggerMultiFileInput = () => {
  multiFileInputRef.value?.click();
};

const handleMultiFileSelect = (e: Event) => {
  const target = e.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    addFilesToList(Array.from(target.files));
    target.value = ''; // 같은 파일 다시 선택 가능하도록
  }
};

const handleMultiDrop = (e: DragEvent) => {
  isDragOver.value = false;
  if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
    const files = Array.from(e.dataTransfer.files).filter(f => 
      f.name.endsWith('.xlsx') || f.name.endsWith('.xls') || f.name.endsWith('.csv')
    );
    if (files.length > 0) {
      addFilesToList(files);
    }
  }
};

const addFilesToList = (files: File[]) => {
  for (const file of files) {
    // 중복 파일 체크
    const exists = uploadFileList.value.some(item => item.file.name === file.name && item.file.size === file.size);
    if (!exists) {
      // 선택된 통장이 있으면 기본값으로 설정
      const defaultBankCode = props.selectedBankCode || (activeBankList.value.length === 1 ? activeBankList.value[0].mb_code : '');
      uploadFileList.value.push({
        file,
        bankCode: defaultBankCode,
        status: 'pending'
      });
    }
  }
};

const removeFileFromList = (index: number) => {
  uploadFileList.value.splice(index, 1);
};

const clearAllFiles = () => {
  uploadFileList.value = [];
};

// 다중 파일 일괄 업로드
const processMultiUpload = async () => {
  if (!canMultiUpload.value) return;
  
  isProcessing.value = true;
  processComplete.value = false;
  
  let totalIncomeCount = 0;
  let totalExpenseCount = 0;
  let successCount = 0;
  let errorCount = 0;
  
  for (let i = 0; i < uploadFileList.value.length; i++) {
    const item = uploadFileList.value[i];
    if (item.status !== 'pending') continue;
    
    item.status = 'processing';
    processStatus.value = { message: `${item.file.name} 처리 중...`, current: i + 1, total: uploadFileList.value.length };
    
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', item.file);
      formDataObj.append('year', props.currentYear);
      formDataObj.append('month', props.currentMonth);
      if (props.bizGubun) formDataObj.append('bizGubun', props.bizGubun);
      appendRequiredBankCode(formDataObj, item.bankCode, item.file.name);
      // 다중 파일 업로드는 자동 매핑된 적요 컬럼 사용 (학습 데이터 기반 계정 추론)
      formDataObj.append('skipMemoSelection', 'true');
      
      const res = await bankbookApi.uploadExcel(props.memberCode, formDataObj);
      
      if (res.data?.success && !res.data?.needMemoSelection) {
        const result = res.data.data || res.data;
        item.status = 'done';
        item.resultCount = (result.incomeCount || 0) + (result.expenseCount || 0);
        totalIncomeCount += result.incomeCount || 0;
        totalExpenseCount += result.expenseCount || 0;
        successCount++;
      } else if (res.data?.needMemoSelection) {
        // 적요 컬럼 선택이 필요한 경우 (다중 업로드에서는 자동 매핑 사용하므로 이 경우는 발생하지 않아야 함)
        item.status = 'error';
        item.errorMessage = '파일 형식 분석 필요 - 단일 업로드 사용';
        errorCount++;
      } else {
        item.status = 'error';
        item.errorMessage = res.data?.message || '업로드 실패';
        errorCount++;
      }
    } catch (error: any) {
      item.status = 'error';
      item.errorMessage = error.response?.data?.message || '업로드 오류';
      errorCount++;
    }
  }
  
  processComplete.value = true;
  processResult.value = {
    incomeCount: totalIncomeCount,
    incomeTotal: 0,
    expenseCount: totalExpenseCount,
    expenseTotal: 0,
    totalCount: totalIncomeCount + totalExpenseCount
  };
  processStatus.value = { 
    message: `완료: ${successCount}개 파일 성공${errorCount > 0 ? `, ${errorCount}개 실패` : ''}` 
  };
  isProcessing.value = false;
  
  if (successCount > 0) {
    emit('saved', []);
  }
};

const handlePriceInput = (e: Event) => {
  const input = e.target as HTMLInputElement;
  const value = input.value.replace(/[^0-9]/g, '');
  formData.value.price = parseInt(value) || 0;
};

const handlePriceKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter') {
    predictFields();
  }
};

const predictFields = async () => {
  if (!canPredict.value || isPredicting.value) return;
  
  isPredicting.value = true;
  prediction.value = null;
  predictionError.value = '';
  
  try {
    const res = await bankbookApi.infer(props.memberCode, {
      gubun: formData.value.gubun,
      memo: formData.value.memo,
      bizGubun: props.bizGubun,
      scope: 'all',
      year: props.currentYear
    });
    
    if (res.data?.data) {
      prediction.value = res.data.data;
    } else {
      predictionError.value = '학습된 데이터가 없습니다.';
    }
  } catch (error: any) {
    console.error('AI 추론 오류:', error);
    predictionError.value = error.response?.data?.message || 'AI 추론에 실패했습니다.';
  } finally {
    isPredicting.value = false;
  }
};

const saveManualInput = async () => {
  if (!canSave.value || isSaving.value) return;
  
  isSaving.value = true;
  try {
    let res;
    
    const baseData = {
      m_code: String(props.memberCode || ''),
      cb_biz_gubun: props.bizGubun,
      cb_bank_code: formData.value.bankCode || undefined,
      cb_gubun: formData.value.gubun as 'IN' | 'OUT',
      cb_date: formData.value.inout_date,
      cb_price: formData.value.price,
      cb_memo: formData.value.memo,
      cb_scode: undefined,
      cb_rel_acc: formData.value.gubun === 'OUT' ? (formData.value.relAcc || undefined) : undefined,
      cb_source: 'manual'
    };
    
    // 삽입 위치에 따라 다른 API 호출
    if (insertPosition.value === 'last' || existingItemsForDate.value.length === 0) {
      // 맨 뒤에 추가 (기본 create API)
      res = await bankbookApi.create(baseData);
    } else if (insertPosition.value === 'first') {
      // 맨 앞에 삽입 (첫 번째 항목 앞에)
      const firstItem = existingItemsForDate.value[0];
      res = await bankbookApi.insertAt({
        targetCbIdx: firstItem.cb_idx,
        position: 'before',
        ...baseData
      });
    } else {
      // 특정 항목 뒤에 삽입
      res = await bankbookApi.insertAt({
        targetCbIdx: insertPosition.value as number,
        position: 'after',
        ...baseData
      });
    }
    
    if (res.data?.data) {
      emit('saved', [res.data.data]);
      closeDialog();
    }
  } catch (error: any) {
    console.error('저장 오류:', error);
    const message = error.response?.data?.message || '저장에 실패했습니다.';
    alert(message);
  } finally {
    isSaving.value = false;
  }
};

const triggerFileInput = () => {
  fileInputRef.value?.click();
};

// ========== 빠른 업로드 관련 함수 ==========
const switchUploadMode = (mode: 'quick' | 'learning') => {
  if (uploadMode.value === mode) return;
  
  // 모드 전환 시 상태 초기화
  uploadMode.value = mode;
  
  if (mode === 'quick') {
    // 학습 모드 상태 초기화
    excelFile.value = null;
    resetAnalysisState();
  } else {
    // 빠른 업로드 상태 초기화
    quickUploadFiles.value = {};
    quickUploadComplete.value = false;
    quickUploadResult.value = { totalCount: 0, successCount: 0, errorCount: 0 };
  }
};

const setQuickFileInputRef = (el: any, bankCode: string) => {
  if (el && el instanceof HTMLInputElement) {
    quickFileInputRefs.value[bankCode] = el;
  }
};

const triggerQuickFileInput = (bankCode: string) => {
  const input = quickFileInputRefs.value[bankCode];
  if (input) {
    input.click();
  }
};

const handleQuickFileSelect = (e: Event, bankCode: string) => {
  const input = e.target as HTMLInputElement;
  if (input.files && input.files[0]) {
    quickUploadFiles.value = {
      ...quickUploadFiles.value,
      [bankCode]: input.files[0]
    };
  }
  // input 초기화 (같은 파일 재선택 가능하도록)
  input.value = '';
};

const removeQuickFile = (bankCode: string) => {
  const newFiles = { ...quickUploadFiles.value };
  delete newFiles[bankCode];
  quickUploadFiles.value = newFiles;
};

const resetQuickUpload = () => {
  quickUploadFiles.value = {};
  quickUploadComplete.value = false;
  quickUploadResult.value = { totalCount: 0, successCount: 0, errorCount: 0 };
  quickUploadStep.value = 'select';
  quickFileAnalyses.value = [];
  currentAnalysisIndex.value = 0;
};

// 파일 분석 (헤더 확인 단계)
const analyzeQuickUploadFiles = async () => {
  if (quickUploadFileCount.value === 0) return;
  
  isAnalyzingFiles.value = true;
  quickFileAnalyses.value = [];
  
  try {
    const entries = Object.entries(quickUploadFiles.value);
    
    for (const [bankCode, file] of entries) {
      const bankInfo = activeBankList.value.find(b => b.mb_code === bankCode);
      
      try {
        // 파일 분석 API 호출
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await bankbookApi.analyzeExcel(props.memberCode, formData);
        
        if (response.data.success && response.data.data) {
          const data = response.data.data;
          const mapping = data.columnMapping || {};
          
          // 적요 컬럼 기본 선택
          let selectedMemoColumns: number[] = [];
          if (mapping.memo !== undefined) {
            selectedMemoColumns = Array.isArray(mapping.memo) ? mapping.memo : [mapping.memo];
          }
          
          // 유효성 검사: 날짜와 금액(입금/출금 또는 금액) 컬럼 필요
          const hasDate = mapping.date !== undefined;
          const hasAmount = mapping.income !== undefined || mapping.expense !== undefined || mapping.amount !== undefined;
          const isValid = hasDate && hasAmount;
          
          quickFileAnalyses.value.push({
            bankCode,
            bankAlias: bankInfo?.mb_alias || bankCode,
            file,
            headers: data.headers || [],
            headerRowIndex: data.headerRowIndex || 0,
            sampleData: data.sampleData || [],
            columnMapping: mapping,
            isValid,
            errorMessage: !isValid ? (hasDate ? '금액 컬럼 미감지' : '날짜 컬럼 미감지') : undefined,
            selectedMemoColumns,
            // 2단계 UI용
            step: 'header',
            previewRows: data.previewRows || [],
            suggestedHeaderRowIndex: data.suggestedHeaderRowIndex,
            selectedHeaderRowIndex: data.suggestedHeaderRowIndex,
            detectionConfidence: data.detectionConfidence || 'low'
          });
        } else {
          // 분석 실패
          quickFileAnalyses.value.push({
            bankCode,
            bankAlias: bankInfo?.mb_alias || bankCode,
            file,
            headers: [],
            headerRowIndex: 0,
            sampleData: [],
            columnMapping: {},
            isValid: false,
            errorMessage: response.data.message || '파일 분석 실패',
            selectedMemoColumns: [],
            step: 'header',
            previewRows: [],
            suggestedHeaderRowIndex: null,
            selectedHeaderRowIndex: null,
            detectionConfidence: 'low'
          });
        }
      } catch (err: any) {
        quickFileAnalyses.value.push({
          bankCode,
          bankAlias: bankInfo?.mb_alias || bankCode,
          file,
          headers: [],
          headerRowIndex: 0,
          sampleData: [],
          columnMapping: {},
          isValid: false,
          errorMessage: err.message || '파일 분석 오류',
          selectedMemoColumns: [],
          step: 'header',
          previewRows: [],
          suggestedHeaderRowIndex: null,
          selectedHeaderRowIndex: null,
          detectionConfidence: 'low'
        });
      }
    }
    
    // 첫 번째 유효하지 않은 파일 또는 첫 번째 파일 펼치기
    const firstInvalidIdx = quickFileAnalyses.value.findIndex(a => !a.isValid);
    currentAnalysisIndex.value = firstInvalidIdx >= 0 ? firstInvalidIdx : 0;
    
    // 헤더 확인 단계로 이동
    quickUploadStep.value = 'confirm';
    
  } finally {
    isAnalyzingFiles.value = false;
  }
};

// 분석 카드 펼치기/접기
const toggleAnalysisCard = (idx: number) => {
  currentAnalysisIndex.value = currentAnalysisIndex.value === idx ? -1 : idx;
};

// 예약된 컬럼인지 확인 (날짜, 금액 등은 적요로 선택 불가)
const isReservedColumn = (analysis: QuickFileAnalysis, colIdx: number): boolean => {
  const m = analysis.columnMapping;
  return colIdx === m.date || 
         colIdx === m.income || 
         colIdx === m.expense || 
         colIdx === m.amount || 
         colIdx === m.balance;
};

/** 샘플표 열 역할 클래스 (색 = 의미) */
const quickSampleColClass = (analysis: QuickFileAnalysis, colIdx: number) => {
  const m = analysis.columnMapping;
  if (analysis.selectedMemoColumns.includes(colIdx)) return 'col-memo';
  if (colIdx === m.date) return 'col-date';
  if (colIdx === m.income) return 'col-income';
  if (colIdx === m.expense) return 'col-expense';
  if (colIdx === m.amount) return 'col-amount';
  if (colIdx === m.balance) return 'col-balance';
  return 'col-plain';
};

const quickSampleRoleLabel = (analysis: QuickFileAnalysis, colIdx: number): string => {
  const m = analysis.columnMapping;
  if (analysis.selectedMemoColumns.includes(colIdx)) return '적요';
  if (colIdx === m.date) return '날짜';
  if (colIdx === m.income) return '입금';
  if (colIdx === m.expense) return '출금';
  if (colIdx === m.amount) return '금액';
  if (colIdx === m.balance) return '잔액';
  return '';
};

// 엑셀 컬럼 이름 생성 (A, B, C, ... Z, AA, AB, ...)
const getExcelColumnName = (index: number): string => {
  let name = '';
  let idx = index;
  while (idx >= 0) {
    name = String.fromCharCode((idx % 26) + 65) + name;
    idx = Math.floor(idx / 26) - 1;
  }
  return name;
};

// 미리보기 행들 중 최대 컬럼 수 계산 (최대 12개로 제한)
const getMaxColumns = (rows: string[][]): number => {
  if (!rows || rows.length === 0) return 0;
  const maxCols = Math.max(...rows.map(row => row.length));
  return Math.min(maxCols, 12);
};

// 헤더 행 선택
const selectHeaderRow = (analysisIdx: number, rowIdx: number) => {
  const analysis = quickFileAnalyses.value[analysisIdx];
  if (!analysis) return;
  
  analysis.selectedHeaderRowIndex = rowIdx;
};

// 헤더 행 확정 후 적요 선택 단계로 이동
const confirmHeaderRow = async (analysisIdx: number) => {
  const analysis = quickFileAnalyses.value[analysisIdx];
  if (!analysis || analysis.selectedHeaderRowIndex === null) return;
  
  // 선택된 헤더 행으로 컬럼 매핑 다시 분석
  const headerRow = analysis.previewRows[analysis.selectedHeaderRowIndex];
  if (!headerRow) return;
  
  // 서버에 다시 분석 요청 (선택된 헤더 행으로)
  try {
    const formData = new FormData();
    formData.append('file', analysis.file);
    formData.append('headerRowIndex', String(analysis.selectedHeaderRowIndex));
    
    const response = await bankbookApi.analyzeExcel(props.memberCode, formData);
    
    if (response.data.success && response.data.data) {
      const data = response.data.data;
      const mapping = data.columnMapping || {};
      
      // 업데이트
      analysis.headers = data.headers || [];
      analysis.headerRowIndex = analysis.selectedHeaderRowIndex;
      analysis.sampleData = data.sampleData || [];
      analysis.columnMapping = mapping;
      
      // 적요 컬럼 기본 선택
      if (mapping.memo !== undefined) {
        analysis.selectedMemoColumns = Array.isArray(mapping.memo) ? mapping.memo : [mapping.memo];
      } else {
        analysis.selectedMemoColumns = [];
      }
      
      // 유효성 검사
      const hasDate = mapping.date !== undefined;
      const hasAmount = mapping.income !== undefined || mapping.expense !== undefined || mapping.amount !== undefined;
      analysis.isValid = hasDate && hasAmount;
      analysis.errorMessage = !analysis.isValid ? (hasDate ? '금액 컬럼 미감지' : '날짜 컬럼 미감지') : undefined;
    }
  } catch (err) {
    console.error('헤더 재분석 오류:', err);
  }
  
  // 적요 선택 단계로 이동
  analysis.step = 'memo';
};

// 적요 컬럼 토글
const toggleQuickMemoColumn = (analysisIdx: number, colIdx: number) => {
  const analysis = quickFileAnalyses.value[analysisIdx];
  if (!analysis) return;
  
  const idx = analysis.selectedMemoColumns.indexOf(colIdx);
  if (idx >= 0) {
    analysis.selectedMemoColumns.splice(idx, 1);
  } else {
    analysis.selectedMemoColumns.push(colIdx);
  }
};

// 매핑 정보 포함하여 업로드
const processQuickUploadWithMapping = async () => {
  const validAnalyses = quickFileAnalyses.value.filter(a => a.isValid);
  if (validAnalyses.length === 0) return;
  
  isQuickUploading.value = true;
  let totalCount = 0;
  let successCount = 0;
  let errorCount = 0;
  const errors: string[] = [];
  const uploadResults: UploadResultInfo[] = [];
  
  try {
    for (const analysis of validAnalyses) {
      try {
        const formData = new FormData();
        formData.append('file', analysis.file);
        appendRequiredBankCode(formData, analysis.bankCode, analysis.bankAlias);
        appendUploadCommonFields(formData);
        formData.append('headerRowIndex', String(analysis.headerRowIndex));
        formData.append('memoColumns', JSON.stringify(analysis.selectedMemoColumns));
        formData.append('columnMapping', JSON.stringify(analysis.columnMapping));
        // 이미 매핑 정보를 보냈으므로 적요 선택 스킵
        formData.append('skipMemoSelection', 'true');
        
        const response = await bankbookApi.uploadExcel(props.memberCode, formData);
        
        if (response.data.success) {
          const resData = response.data.data || response.data;
          const count = resData.totalCount || resData.insertedCount || 0;
          totalCount += count;
          successCount++;
          
          // 성공 결과 저장
          uploadResults.push({
            bankCode: analysis.bankCode,
            bankAlias: analysis.bankAlias,
            fileName: analysis.file.name,
            count,
            success: true
          });
        } else {
          errorCount++;
          const errMsg = response.data.message || '업로드 실패';
          errors.push(`${analysis.bankAlias}: ${errMsg}`);
          console.error(`[빠른 업로드] ${analysis.bankCode} 실패:`, response.data.message);
          
          // 실패 결과 저장
          uploadResults.push({
            bankCode: analysis.bankCode,
            bankAlias: analysis.bankAlias,
            fileName: analysis.file.name,
            count: 0,
            success: false,
            message: errMsg
          });
        }
      } catch (err: any) {
        errorCount++;
        const errMsg = err.message || '네트워크 오류';
        errors.push(`${analysis.bankAlias}: ${errMsg}`);
        console.error(`[빠른 업로드] ${analysis.bankCode} 에러:`, err);
        
        // 에러 결과 저장
        uploadResults.push({
          bankCode: analysis.bankCode,
          bankAlias: analysis.bankAlias,
          fileName: analysis.file.name,
          count: 0,
          success: false,
          message: errMsg
        });
      }
    }
    
    quickUploadResult.value = { totalCount, successCount, errorCount };
    
    if (errors.length > 0) {
      console.warn('[빠른 업로드] 일부 실패:', errors);
    }
    
    // 업로드 완료 시 부모에 알림 + 모달 닫기
    if (successCount > 0) {
      emit('saved', []);
    }
    
    // 업로드 결과 상세 정보 전달 및 모달 닫기
    emit('uploadComplete', uploadResults);
    dialogOpen.value = false;
    
  } finally {
    isQuickUploading.value = false;
  }
};

// 기존 빠른 업로드 (사용 안 함 - 호환성 유지)
const processQuickUpload = async () => {
  // 이제 analyzeQuickUploadFiles -> processQuickUploadWithMapping 흐름 사용
  await analyzeQuickUploadFiles();
};

// 분석 상태 초기화 (파일 변경/제거 시)
const resetAnalysisState = () => {
  // 헤더 선택 초기화
  needHeaderSelection.value = false;
  headerPreviewData.value = null;
  selectedHeaderRowIndex.value = null;
  // 적요 컬럼 선택 초기화
  needMemoSelection.value = false;
  memoSelectionData.value = null;
  selectedMemoColumns.value = [];
  selectedMemoCells.value = {};
  // 진행 상태 초기화
  isProcessing.value = false;
  processComplete.value = false;
  processStatus.value = { message: '' };
};

const handleFileSelect = (e: Event) => {
  const input = e.target as HTMLInputElement;
  if (input.files && input.files[0]) {
    // 파일이 변경되면 분석 상태 초기화
    resetAnalysisState();
    excelFile.value = input.files[0];
  }
};

const handleDrop = (e: DragEvent) => {
  isDragOver.value = false;
  if (e.dataTransfer?.files && e.dataTransfer.files[0]) {
    const file = e.dataTransfer.files[0];
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (['xlsx', 'xls', 'csv'].includes(ext || '')) {
      // 파일이 변경되면 분석 상태 초기화
      resetAnalysisState();
      excelFile.value = file;
    } else {
      alert('엑셀 파일(.xlsx, .xls) 또는 CSV 파일만 업로드 가능합니다.');
    }
  }
};

const removeFile = () => {
  excelFile.value = null;
  resetAnalysisState();
  // 파일 입력 초기화
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

// 파일 미리보기 로드 (헤더 행 선택용)
const loadFilePreview = async () => {
  if (!excelFile.value) return;
  
  isLoadingPreview.value = true;
  
  try {
    const formDataObj = new FormData();
    formDataObj.append('file', excelFile.value);
    formDataObj.append('previewOnly', 'true');
    formDataObj.append('sheetIndex', String(selectedSheetIndex.value));
    
    const res = await bankbookApi.uploadExcel(props.memberCode, formDataObj);
    
    if (res.data?.previewData) {
      headerPreviewData.value = res.data.previewData;
      needHeaderSelection.value = true;
      selectedHeaderRowIndex.value = res.data.suggestedHeaderRow ?? null;
      // 시트 목록 저장
      if (res.data.sheetNames) {
        sheetNames.value = res.data.sheetNames;
      }
    } else {
      // 미리보기 데이터가 없으면 바로 분석 진행
      processExcelData(false);
    }
  } catch (error) {
    console.error('미리보기 로드 실패:', error);
    // 실패 시 바로 분석 진행
    processExcelData(false);
  } finally {
    isLoadingPreview.value = false;
  }
};

// 시트 변경 시 미리보기 다시 로드
const onSheetChange = async (index: number) => {
  selectedSheetIndex.value = index;
  selectedHeaderRowIndex.value = null;
  await loadFilePreview();
};

const selectLearningHeaderRow = (rowIdx: number) => {
  selectedHeaderRowIndex.value = rowIdx;
};

const cancelHeaderSelection = () => {
  needHeaderSelection.value = false;
  headerPreviewData.value = null;
  selectedHeaderRowIndex.value = null;
  sheetNames.value = [];
  selectedSheetIndex.value = 0;
};

const confirmHeaderSelection = async () => {
  if (selectedHeaderRowIndex.value === null) return;
  
  needHeaderSelection.value = false;
  // 선택한 헤더 행 인덱스를 사용하여 분석 진행
  await processExcelDataWithHeader(selectedHeaderRowIndex.value);
};

const processExcelDataWithHeader = async (headerRowIndex: number) => {
  if (!excelFile.value) return;
  if (!canUpload.value) {
    alert('통장을 선택해주세요.');
    return;
  }
  
  isProcessing.value = true;
  processComplete.value = false;
  processStatus.value = { message: '파일 분석 중...' };
  
  try {
    const formDataObj = new FormData();
    formDataObj.append('file', excelFile.value);
    appendUploadCommonFields(formDataObj);
    formDataObj.append('headerRowIndex', headerRowIndex.toString());
    formDataObj.append('sheetIndex', String(selectedSheetIndex.value));
    appendRequiredBankCode(formDataObj, selectedUploadBankCode.value);
    
    const res = await bankbookApi.uploadExcel(props.memberCode, formDataObj);
    
    if (res.data?.needMemoSelection) {
      isProcessing.value = false;
      needMemoSelection.value = true;
      memoSelectionData.value = {
        headers: res.data.headers,
        sampleData: res.data.sampleData,
        headerRowIndex: res.data.headerRowIndex,
        columnMapping: res.data.columnMapping,
        autoMemoIndex: res.data.autoMemoIndex
      };
      if (res.data.autoMemoIndex !== undefined && res.data.autoMemoIndex !== null) {
        selectedMemoColumns.value = [res.data.autoMemoIndex];
      } else {
        selectedMemoColumns.value = [];
      }
      return;
    }
    
    if (res.data?.data) {
      const result = res.data.data;
      processResult.value = {
        incomeCount: result.incomeCount || 0,
        incomeTotal: result.incomeTotal || 0,
        expenseCount: result.expenseCount || 0,
        expenseTotal: result.expenseTotal || 0,
        totalCount: result.totalCount || 0
      };
      processStatus.value = { message: '처리 완료!', current: result.totalCount, total: result.totalCount };
      processComplete.value = true;
      
      // 항상 saved 이벤트 emit (부모에서 새로고침하도록)
      emit('saved', result.items || []);
    }
  } catch (error: any) {
    console.error('처리 오류:', error);
    processStatus.value = { message: error.response?.data?.message || '처리 실패' };
  } finally {
    if (!processComplete.value && !needMemoSelection.value) {
      isProcessing.value = false;
    }
  }
};

const processExcelData = async (withMemoColumns = false) => {
  if (!excelFile.value) return;
  if (!canUpload.value) {
    alert('통장을 선택해주세요.');
    return;
  }
  
  isProcessing.value = true;
  processComplete.value = false;
  processStatus.value = { message: '파일 분석 중...' };
  
  try {
    const formDataObj = new FormData();
    formDataObj.append('file', excelFile.value);
    appendUploadCommonFields(formDataObj);
    appendRequiredBankCode(formDataObj, selectedUploadBankCode.value);
    
    if (withMemoColumns && selectedMemoColumns.value.length > 0) {
      formDataObj.append('memoColumns', JSON.stringify(selectedMemoColumns.value));
      if (Object.keys(selectedMemoCells.value).length > 0) {
        formDataObj.append('memoCellOverrides', JSON.stringify(selectedMemoCells.value));
      }
      if (memoSelectionData.value?.headerRowIndex !== undefined) {
        formDataObj.append('headerRowIndex', String(memoSelectionData.value.headerRowIndex));
      }
    }
    
    const res = await bankbookApi.uploadExcel(props.memberCode, formDataObj);
    
    if (res.data?.needMemoSelection) {
      isProcessing.value = false;
      needMemoSelection.value = true;
      memoSelectionData.value = {
        headers: res.data.headers,
        sampleData: res.data.sampleData,
        headerRowIndex: res.data.headerRowIndex,
        columnMapping: res.data.columnMapping,
        autoMemoIndex: res.data.autoMemoIndex
      };
      if (res.data.autoMemoIndex !== undefined && res.data.autoMemoIndex !== null) {
        selectedMemoColumns.value = [res.data.autoMemoIndex];
      } else {
        selectedMemoColumns.value = [];
      }
      return;
    }
    
    if (res.data?.data) {
      const result = res.data.data;
      processResult.value = {
        incomeCount: result.incomeCount || 0,
        incomeTotal: result.incomeTotal || 0,
        expenseCount: result.expenseCount || 0,
        expenseTotal: result.expenseTotal || 0,
        totalCount: result.totalCount || 0
      };
      processStatus.value = {
        message: '처리 완료!',
        current: result.totalCount,
        total: result.totalCount
      };
      processComplete.value = true;
      
      // 항상 saved 이벤트 emit (부모에서 새로고침하도록)
      emit('saved', result.items || []);
    }
  } catch (error: any) {
    console.error('엑셀 처리 오류:', error);
    processStatus.value = { message: error.response?.data?.message || '처리 실패' };
  } finally {
    if (!processComplete.value && !needMemoSelection.value) {
      isProcessing.value = false;
    }
  }
};

const toggleMemoColumn = (index: number) => {
  const idx = selectedMemoColumns.value.indexOf(index);
  
  // 해당 컬럼의 개별 셀 오버라이드 모두 제거
  Object.keys(selectedMemoCells.value).forEach(rowIdxStr => {
    const rowIdx = parseInt(rowIdxStr);
    if (selectedMemoCells.value[rowIdx]?.[index] !== undefined) {
      delete selectedMemoCells.value[rowIdx][index];
      // 해당 행에 더 이상 오버라이드가 없으면 행 자체를 삭제
      if (Object.keys(selectedMemoCells.value[rowIdx]).length === 0) {
        delete selectedMemoCells.value[rowIdx];
      }
    }
  });
  // 반응성 트리거
  selectedMemoCells.value = { ...selectedMemoCells.value };
  
  if (idx === -1) {
    selectedMemoColumns.value.push(index);
  } else {
    selectedMemoColumns.value.splice(idx, 1);
  }
};

const isCellSelected = (rowIdx: number, colIdx: number): boolean => {
  const isColumnSelected = selectedMemoColumns.value.includes(colIdx);
  const cellOverride = selectedMemoCells.value[rowIdx]?.[colIdx];
  if (cellOverride !== undefined) return cellOverride;
  return isColumnSelected;
};

const hasRowOverride = (rowIdx: number): boolean => {
  return selectedMemoCells.value[rowIdx] !== undefined && 
         Object.keys(selectedMemoCells.value[rowIdx]).length > 0;
};

const toggleCellSelection = (rowIdx: number, colIdx: number) => {
  const isColumnSelected = selectedMemoColumns.value.includes(colIdx);
  const currentCellOverride = selectedMemoCells.value[rowIdx]?.[colIdx];
  
  // 현재 셀의 실제 선택 상태
  const currentState = currentCellOverride !== undefined ? currentCellOverride : isColumnSelected;
  // 토글 후 새로운 상태
  const newState = !currentState;
  
  // 새로운 상태가 컬럼 기본 선택 상태와 같으면 오버라이드 제거
  if (newState === isColumnSelected) {
    // 오버라이드 제거
    if (selectedMemoCells.value[rowIdx]) {
      delete selectedMemoCells.value[rowIdx][colIdx];
      // 해당 행에 더 이상 오버라이드가 없으면 행 자체를 삭제
      if (Object.keys(selectedMemoCells.value[rowIdx]).length === 0) {
        delete selectedMemoCells.value[rowIdx];
      }
    }
  } else {
    // 오버라이드 추가
    if (!selectedMemoCells.value[rowIdx]) {
      selectedMemoCells.value[rowIdx] = {};
    }
    selectedMemoCells.value[rowIdx][colIdx] = newState;
  }
  
  // 트리거 반응성
  selectedMemoCells.value = { ...selectedMemoCells.value };
};

const resetCellSelection = () => {
  selectedMemoCells.value = {};
};

const getCellMemoResult = (rowIdx: number, row: string[]): string => {
  const headers = memoSelectionData.value?.headers || [];
  const activeCols: number[] = [];
  
  headers.forEach((header) => {
    const colIdx = header.index;
    if (isCellSelected(rowIdx, colIdx)) {
      activeCols.push(colIdx);
    }
  });
  
  if (activeCols.length === 0) return '';
  
  return activeCols
    .sort((a, b) => a - b)
    .map(colIdx => row[colIdx])
    .filter(v => v && v.trim())
    .join(' ') || '';
};

const cancelMemoSelection = () => {
  needMemoSelection.value = false;
  memoSelectionData.value = null;
  selectedMemoColumns.value = [];
  selectedMemoCells.value = {};
  isProcessing.value = false;
};

// ============================================
// 검색 관련 메서드
// ============================================

const applyGroupSearch = () => {
  // 검색어 입력 후 엔터 시 처리 (현재는 실시간 검색이므로 별도 처리 없음)
};

const clearGroupSearch = () => {
  groupSearchQuery.value = '';
};

const isSearchMatch = (group: MemoGroup): boolean => {
  if (!groupSearchQuery.value.trim()) return false;
  const query = groupSearchQuery.value.toLowerCase().trim();
  if (group.keyword.toLowerCase().includes(query)) return true;
  return group.memos.some(m => m.memo.toLowerCase().includes(query));
};

const getOriginalIndex = (group: MemoGroup): number => {
  return memoGroups.value.findIndex(g => g.keyword === group.keyword && g.gubun === group.gubun);
};

const openBulkAssignModal = () => {
  bulkAssignGubun.value = 'all';
  bulkAssignScode.value = null;
  bulkRegisterAsKeyword.value = true;
  showBulkAssignModal.value = true;
};

const applyBulkAssign = () => {
  if (!bulkAssignScode.value) return;
  
  const targetGroups = searchResultGroups.value.filter(g => {
    if (bulkAssignGubun.value === 'all') return true;
    return g.gubun === bulkAssignGubun.value;
  });
  
  // 각 그룹에 계정 적용 (새 배열로 교체하여 반응성 보장)
  const updatedGroups = [...memoGroups.value];
  targetGroups.forEach(group => {
    const idx = getOriginalIndex(group);
    if (idx !== -1) {
      updatedGroups[idx] = { ...updatedGroups[idx], selectedScode: bulkAssignScode.value! };
    }
  });
  memoGroups.value = updatedGroups;
  
  // 키워드 등록 옵션이 켜져 있으면 registeredKeywords에 추가
  if (bulkRegisterAsKeyword.value && groupSearchQuery.value.trim()) {
    const scodeName = getScodeName(bulkAssignScode.value, bulkAssignGubun.value === 'IN' ? 'IN' : 'OUT');
    
    // 수입/지출 각각에 대해 키워드 등록
    if (bulkAssignGubun.value === 'all') {
      // 수입과 지출 모두에 등록
      const hasIncome = targetGroups.some(g => g.gubun === 'IN');
      const hasExpense = targetGroups.some(g => g.gubun === 'OUT');
      
      if (hasIncome) {
        const exists = registeredKeywords.value.some(
          rk => rk.keyword === groupSearchQuery.value.trim() && rk.gubun === 'IN'
        );
        if (!exists) {
          registeredKeywords.value.push({
            gubun: 'IN',
            keyword: groupSearchQuery.value.trim(),
            scode: bulkAssignScode.value!,
            scodeName: scodeName
          });
        }
      }
      if (hasExpense) {
        const exists = registeredKeywords.value.some(
          rk => rk.keyword === groupSearchQuery.value.trim() && rk.gubun === 'OUT'
        );
        if (!exists) {
          registeredKeywords.value.push({
            gubun: 'OUT',
            keyword: groupSearchQuery.value.trim(),
            scode: bulkAssignScode.value!,
            scodeName: scodeName
          });
        }
      }
    } else {
      const exists = registeredKeywords.value.some(
        rk => rk.keyword === groupSearchQuery.value.trim() && rk.gubun === bulkAssignGubun.value
      );
      if (!exists) {
        registeredKeywords.value.push({
          gubun: bulkAssignGubun.value as 'IN' | 'OUT',
          keyword: groupSearchQuery.value.trim(),
          scode: bulkAssignScode.value!,
          scodeName: scodeName
        });
      }
    }
  }
  
  showBulkAssignModal.value = false;
  
  // 저장 완료 스낵바 표시
  saveSnackbarMessage.value = `${targetGroups.length}개 그룹에 계정이 지정되었습니다`;
  showSaveSnackbar.value = true;
};

const getScodeName = (scode: string | null, gubun: 'IN' | 'OUT'): string => {
  if (!scode) return '';
  const list = gubun === 'IN' ? incomeCodeList.value : expenseCodeList.value;
  const found = list.find(item => item.scode === scode);
  return found?.sname || scode;
};

const showLearningStep = async () => {
  if (selectedMemoColumns.value.length === 0) {
    alert('적요 컬럼을 하나 이상 선택해주세요.');
    return;
  }
  
  const memos: Array<{ memo: string; gubun: string; price: number; date?: string }> = [];
  const sampleData = memoSelectionData.value?.sampleData || [];
  const columnMapping = memoSelectionData.value?.columnMapping || {};
  
  sampleData.forEach((row, rowIdx) => {
    const memo = getCellMemoResult(rowIdx, row);
    if (!memo) return;
    
    let gubun = 'OUT';
    let price = 0;
    let date = '';
    
    // 날짜 추출
    if (columnMapping.date !== undefined) {
      const dateVal = row[columnMapping.date];
      if (dateVal) {
        date = String(dateVal);
      }
    }
    
    if (columnMapping.income !== undefined && columnMapping.expense !== undefined) {
      const incomeVal = parseFloat(String(row[columnMapping.income] || '0').replace(/[^0-9.-]/g, ''));
      const expenseVal = parseFloat(String(row[columnMapping.expense] || '0').replace(/[^0-9.-]/g, ''));
      
      if (incomeVal !== 0) {
        // 입금 컬럼 → 수입 (마이너스도 수입으로 유지)
        gubun = 'IN';
        price = incomeVal; // 마이너스 그대로 유지
      } else if (expenseVal !== 0) {
        // 출금 컬럼 → 지출 (마이너스도 지출로 유지)
        gubun = 'OUT';
        price = expenseVal; // 마이너스 그대로 유지
      }
    } else if (columnMapping.amount !== undefined) {
      const amountVal = parseFloat(String(row[columnMapping.amount] || '0').replace(/[^0-9.-]/g, ''));
      price = amountVal; // 마이너스 그대로 유지
      // 음수면 지출, 양수면 수입으로 추정 (단일 금액 컬럼)
      gubun = amountVal < 0 ? 'OUT' : 'IN';
    }
    
    memos.push({ memo, gubun, price, date });
  });
  
  if (memos.length === 0) {
    alert('추출된 적요가 없습니다.');
    return;
  }
  
  isLoadingGroups.value = true;
  try {
    const res = await bankbookApi.groupMemos(props.memberCode, {
      memos,
      bizGubun: props.bizGubun,
      scope: learningScope.value,
      year: props.currentYear
    });
    
    if (res.data?.data?.groups) {
      memoGroups.value = res.data.data.groups.map((g: any) => {
        // 기존에 등록한 키워드가 있으면 해당 계정 적용
        const registeredMatch = registeredKeywords.value.find(
          rk => rk.keyword === g.keyword && rk.gubun === g.gubun
        );
        
        return {
          ...g,
          selectedScode: registeredMatch?.scode || g.learned?.scode || undefined,
          isEditing: false,
          customKeywords: []
        };
      });
      
      needMemoSelection.value = false;
      currentStep.value = 'learning';
      selectedGroupIdx.value = memoGroups.value.length > 0 ? 0 : null;
    }
  } catch (error: any) {
    console.error('적요 그룹화 오류:', error);
    alert('적요 그룹화에 실패했습니다.');
  } finally {
    isLoadingGroups.value = false;
  }
};

const changeLearningScope = async (scope: 'facility' | 'bizGubun' | 'all') => {
  learningScope.value = scope;
  
  const memos = memoGroups.value.flatMap(g => g.memos);
  if (memos.length === 0) return;
  
  isLoadingGroups.value = true;
  try {
    const res = await bankbookApi.groupMemos(props.memberCode, {
      memos,
      bizGubun: props.bizGubun,
      scope,
      year: props.currentYear
    });
    
    if (res.data?.data?.groups) {
      const prevSelections: Record<string, { scode: string; keywords: string[] }> = {};
      memoGroups.value.forEach(g => {
        const key = `${g.gubun}:${g.keyword}`;
        prevSelections[key] = {
          scode: g.selectedScode || '',
          keywords: g.customKeywords || []
        };
      });
      
      memoGroups.value = res.data.data.groups.map((g: any) => {
        const key = `${g.gubun}:${g.keyword}`;
        const prev = prevSelections[key];
        return {
          ...g,
          selectedScode: prev?.scode || g.learned?.scode || undefined,
          isEditing: false,
          customKeywords: prev?.keywords || []
        };
      });
      
      if (selectedGroupIdx.value !== null && selectedGroupIdx.value >= memoGroups.value.length) {
        selectedGroupIdx.value = memoGroups.value.length > 0 ? 0 : null;
      }
    }
  } catch (error) {
    console.error('범위 변경 오류:', error);
  } finally {
    isLoadingGroups.value = false;
  }
};

const getLearningSourceLabel = (source: string): string => {
  switch (source) {
    case 'facility': return '이 시설';
    case 'otherFacility': return '다른 시설';
    case 'allBizGubun': return '다른 사업';
    default: return source;
  }
};

const selectGroup = (idx: number) => {
  selectedGroupIdx.value = idx;
};

// 그룹 카드 클릭 핸들러
const onGroupCardClick = (group: MemoGroup, event: MouseEvent) => {
  const idx = getOriginalIndex(group);
  if (isMultiSelectMode.value) {
    toggleGroupSelection(idx);
  } else {
    selectGroup(idx);
  }
};

// 다중 선택 모드 변경 시
const onMultiSelectModeChange = () => {
  if (!isMultiSelectMode.value) {
    selectedGroupIndices.value.clear();
  }
};

// 그룹 선택 토글
const toggleGroupSelection = (idx: number) => {
  if (selectedGroupIndices.value.has(idx)) {
    selectedGroupIndices.value.delete(idx);
  } else {
    selectedGroupIndices.value.add(idx);
  }
  // 반응성 트리거를 위해 새 Set 생성
  selectedGroupIndices.value = new Set(selectedGroupIndices.value);
};

// 전체 선택/해제 토글
const toggleSelectAll = () => {
  if (isAllDisplayedSelected.value) {
    // 전체 해제
    displayedGroups.value.forEach(group => {
      selectedGroupIndices.value.delete(getOriginalIndex(group));
    });
  } else {
    // 전체 선택
    displayedGroups.value.forEach(group => {
      selectedGroupIndices.value.add(getOriginalIndex(group));
    });
  }
  selectedGroupIndices.value = new Set(selectedGroupIndices.value);
};

// 다중 선택 일괄 지정 모달 열기
const openBulkAssignSelectedModal = () => {
  if (selectedGroupIndices.value.size === 0) return;
  bulkSelectedGubun.value = 'all';
  bulkSelectedScode.value = null;
  showBulkAssignSelectedModal.value = true;
};

// 다중 선택 일괄 계정 지정 적용
const applyBulkAssignSelected = () => {
  if (!bulkSelectedScode.value) return;
  
  let appliedCount = 0;
  const updatedGroups = [...memoGroups.value];
  
  selectedGroupIndices.value.forEach(idx => {
    const group = updatedGroups[idx];
    if (!group) return;
    
    // 구분 필터 적용
    if (bulkSelectedGubun.value !== 'all' && group.gubun !== bulkSelectedGubun.value) return;
    
    // 계정 지정 (새 객체로 교체하여 반응성 보장)
    updatedGroups[idx] = { ...group, selectedScode: bulkSelectedScode.value ?? undefined };
    appliedCount++;
  });
  
  // 배열 전체를 새로 할당하여 반응성 트리거
  memoGroups.value = updatedGroups;
  
  // 선택 초기화 및 모달 닫기
  selectedGroupIndices.value.clear();
  selectedGroupIndices.value = new Set();
  showBulkAssignSelectedModal.value = false;
  isMultiSelectMode.value = false;
  
  // 저장 완료 스낵바 표시
  saveSnackbarMessage.value = `${appliedCount}개 그룹에 계정이 지정되었습니다`;
  showSaveSnackbar.value = true;
};

const getScodeNameForGroup = (group: MemoGroup): string => {
  if (group.selectedScode) {
    const list = group.gubun === 'IN' ? incomeCodeList.value : expenseCodeList.value;
    const found = list.find(c => c.scode === group.selectedScode);
    return found?.sname || group.selectedScode;
  }
  return group.learned?.scode_name || group.learned?.scode || '';
};

const onScodeSelect = (idx: number) => {
  const group = memoGroups.value[idx];
  if (!group?.selectedScode) return;
  
  const scodeName = getScodeNameForGroup(group);
  
  // 다중 선택 모드이고, 선택된 항목이 있으면 모두에게 적용
  if (isMultiSelectMode.value && selectedGroupIndices.value.size > 0) {
    // 선택된 항목 중 같은 구분(수입/지출)인 것들에만 적용
    const updatedGroups = [...memoGroups.value];
    let appliedCount = 0;
    
    selectedGroupIndices.value.forEach(selectedIdx => {
      const targetGroup = updatedGroups[selectedIdx];
      if (targetGroup && targetGroup.gubun === group.gubun) {
        updatedGroups[selectedIdx] = { ...targetGroup, selectedScode: group.selectedScode };
        appliedCount++;
      }
    });
    
    memoGroups.value = updatedGroups;
    
    // 선택 초기화
    selectedGroupIndices.value.clear();
    selectedGroupIndices.value = new Set();
    isMultiSelectMode.value = false;
    
    saveSnackbarMessage.value = `${appliedCount}개 그룹에 "${scodeName}" 지정됨`;
  } else {
    saveSnackbarMessage.value = `"${group.keyword}" → ${scodeName} 지정됨`;
  }
  
  showSaveSnackbar.value = true;
};

// 등록된 키워드 삭제
const removeRegisteredKeyword = (idx: number) => {
  registeredKeywords.value.splice(idx, 1);
};

const backToMemoSelection = () => {
  currentStep.value = 'upload';
  needMemoSelection.value = true;
  selectedGroupIdx.value = null;
};

// 확인 다이얼로그 표시
const showConfirmDialog = (type: 'skip' | 'saveAndRegister') => {
  confirmDialogType.value = type;
  confirmDialogOpen.value = true;
};

// 확인된 액션 실행
const executeConfirmedAction = () => {
  confirmDialogOpen.value = false;
  if (confirmDialogType.value === 'skip') {
    skipLearningAndRegister();
  } else {
    saveLearnAndRegister();
  }
};

const skipLearningAndRegister = async () => {
  isProcessing.value = true;
  processStatus.value = { message: '데이터 등록 중...' };
  
  try {
    const formDataObj = new FormData();
    formDataObj.append('file', excelFile.value!);
    appendUploadCommonFields(formDataObj);
    appendRequiredBankCode(formDataObj, selectedUploadBankCode.value);
    formDataObj.append('memoColumns', JSON.stringify(selectedMemoColumns.value));
    if (Object.keys(selectedMemoCells.value).length > 0) {
      formDataObj.append('memoCellOverrides', JSON.stringify(selectedMemoCells.value));
    }
    if (memoSelectionData.value?.headerRowIndex !== undefined) {
      formDataObj.append('headerRowIndex', String(memoSelectionData.value.headerRowIndex));
    }
    
    const res = await bankbookApi.uploadExcel(props.memberCode, formDataObj);
    
    if (res.data?.data) {
      const result = res.data.data;
      processResult.value = {
        incomeCount: result.incomeCount || 0,
        incomeTotal: result.incomeTotal || 0,
        expenseCount: result.expenseCount || 0,
        expenseTotal: result.expenseTotal || 0,
        totalCount: result.totalCount || 0
      };
      processStatus.value = { message: '처리 완료!', current: result.totalCount, total: result.totalCount };
      processComplete.value = true;
      
      // 항상 saved 이벤트 emit (부모에서 새로고침하도록)
      emit('saved', result.items || []);
      
      // 완료 후 모달 자동 닫기
      setTimeout(() => {
        closeDialog();
      }, 500);
    }
  } catch (error: any) {
    console.error('처리 오류:', error);
    processStatus.value = { message: '처리 실패' };
  } finally {
    if (!processComplete.value) {
      isProcessing.value = false;
    }
  }
};

const saveLearnAndRegister = async () => {
  isProcessing.value = true;
  processComplete.value = false;
  processStatus.value = { message: '학습 데이터 저장 중...' };
  
  try {
    // 1. 학습 데이터 저장
    const learnItems: Array<{
      m_code: string | number;
      biz_gubun?: string;
      gubun: string;
      keyword: string;
      scode: string;
    }> = [];
    
    // 적요 그룹에서 선택한 계정
    memoGroups.value.forEach(group => {
      const scode = group.selectedScode || group.learned?.scode;
      if (scode) {
        // 기본 키워드 (적요 전체)
        learnItems.push({
          m_code: props.memberCode,
          biz_gubun: props.bizGubun,
          gubun: group.gubun,
          keyword: group.keyword,
          scode
        });
      }
    });
    
    // 직접 등록한 키워드들
    registeredKeywords.value.forEach(rk => {
      learnItems.push({
        m_code: props.memberCode,
        biz_gubun: props.bizGubun,
        gubun: rk.gubun,
        keyword: rk.keyword,
        scode: rk.scode
      });
    });
    
    if (learnItems.length > 0) {
      await bankbookApi.learnBatch(learnItems);
    }
    
    // 2. 엑셀 데이터 등록
    processStatus.value = { message: '데이터 등록 중...' };
    
    const formDataObj = new FormData();
    formDataObj.append('file', excelFile.value!);
    appendUploadCommonFields(formDataObj);
    appendRequiredBankCode(formDataObj, selectedUploadBankCode.value);
    formDataObj.append('memoColumns', JSON.stringify(selectedMemoColumns.value));
    if (Object.keys(selectedMemoCells.value).length > 0) {
      formDataObj.append('memoCellOverrides', JSON.stringify(selectedMemoCells.value));
    }
    if (memoSelectionData.value?.headerRowIndex !== undefined) {
      formDataObj.append('headerRowIndex', String(memoSelectionData.value.headerRowIndex));
    }
    
    const res = await bankbookApi.uploadExcel(props.memberCode, formDataObj);
    
    if (res.data?.data) {
      const result = res.data.data;
      processResult.value = {
        incomeCount: result.incomeCount || 0,
        incomeTotal: result.incomeTotal || 0,
        expenseCount: result.expenseCount || 0,
        expenseTotal: result.expenseTotal || 0,
        totalCount: result.totalCount || 0
      };
      processStatus.value = { message: '처리 완료!', current: result.totalCount, total: result.totalCount };
      processComplete.value = true;
      
      // 항상 saved 이벤트 emit (부모에서 새로고침하도록)
      emit('saved', result.items || []);
      
      // 완료 후 모달 자동 닫기
      setTimeout(() => {
        closeDialog();
      }, 500);
    }
  } catch (error: any) {
    console.error('처리 오류:', error);
    processStatus.value = { message: '처리 실패' };
  } finally {
    if (!processComplete.value) {
      isProcessing.value = false;
    }
  }
};

const closeDialog = () => {
  if (isProcessing.value && !processComplete.value) return;
  
  dialogOpen.value = false;
  
  setTimeout(() => {
    activeTab.value = 'excel';
    currentStep.value = 'upload';
    formData.value = {
      gubun: 'OUT',
      inout_date: `${props.currentYear}-${props.currentMonth}-01`,
      price: 0,
      memo: '',
      relAcc: '',
      bankCode: props.selectedBankCode || ''
    };
    prediction.value = null;
    predictionError.value = '';
    excelFile.value = null;
    isProcessing.value = false;
    processComplete.value = false;
    processStatus.value = { message: '' };
    needHeaderSelection.value = false;
    headerPreviewData.value = null;
    selectedHeaderRowIndex.value = null;
    needMemoSelection.value = false;
    memoSelectionData.value = null;
    selectedMemoColumns.value = [];
    selectedMemoCells.value = {};
    showLearning.value = false;
    memoGroups.value = [];
    learningScope.value = 'facility';
    selectedGroupIdx.value = null;
    groupFilter.value = 'all';
    registeredKeywords.value = [];
    groupSearchQuery.value = '';
    showBulkAssignModal.value = false;
    bulkAssignGubun.value = 'all';
    bulkAssignScode.value = null;
    bulkRegisterAsKeyword.value = true;
    // 통장 선택은 초기화하지 않음 (다음에 열 때 props에서 다시 설정됨)
    // 다중 파일 목록 초기화
    uploadFileList.value = [];
    // 빠른 업로드 상태 초기화
    quickUploadFiles.value = {};
    quickUploadComplete.value = false;
    quickUploadResult.value = { totalCount: 0, successCount: 0, errorCount: 0 };
    // 업로드 모드는 빠른 업로드로 초기화
    uploadMode.value = 'quick';
  }, 300);
};

watch(dialogOpen, (newVal) => {
  if (newVal) {
    formData.value.inout_date = `${props.currentYear}-${props.currentMonth}-01`;
    uploadBankUserTouched.value = false;

    // 다이얼로그 열릴 때만 초기 통장 설정 (사이드바 선택 → 기본값, 이후 사용자 변경 유지)
    let defaultBankCode = '';
    if (props.selectedBankCode) {
      selectedUploadBankCode.value = props.selectedBankCode;
      defaultBankCode = props.selectedBankCode;
    } else if (activeBankList.value.length === 1) {
      defaultBankCode = activeBankList.value[0].mb_code;
      selectedUploadBankCode.value = defaultBankCode;
    } else {
      selectedUploadBankCode.value = null;
    }
    formData.value.bankCode = defaultBankCode;
    // 다중 파일 목록 및 상태 초기화
    uploadFileList.value = [];
    processComplete.value = false;
    processStatus.value = { message: '' };
    processResult.value = { incomeCount: 0, incomeTotal: 0, expenseCount: 0, expenseTotal: 0, totalCount: 0 };
  }
});

</script>

<style lang="scss">
@import '@/assets/styles/variables';
@import '@/assets/styles/components/bankbook-upload';
</style>
